import {
  useSearchAdresse
} from "./chunk.BNCPJJY3.js";
import {
  CardHeightEnum,
  CardImageAlignmentEnum,
  CardOrientationEnum,
  DeliverabilityChartBarStatusTranslation,
  FreeTextFieldMode
} from "./chunk.LHTKCKFZ.js";

// src/lib/core/constants/predefined-regex.ts
var PREDEFINED_REGEX = [
  {
    id: "french_postal_code",
    label: "Code postal fran\xE7ais",
    pattern: "^(?:0[1-9]|[1-8]\\d|9[0-8])\\d{3}$"
  },
  {
    id: "phone_number",
    label: "Num\xE9ro de t\xE9l\xE9phone",
    pattern: "^(?:(?:\\+|00)33[\\s.-]?|0)[1-9](?:[\\s.-]?\\d{2}){4}$"
  },
  {
    id: "email",
    label: "Adresse e-mail",
    pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
  }
];
function getPredefinedRegexById(id) {
  return PREDEFINED_REGEX.find((regex) => regex.id === id);
}
function isPredefinedRegexType(type) {
  return PREDEFINED_REGEX.some((regex) => regex.id === type);
}

// src/lib/components/chart-bar/chart-bar.tsx
import { Chart } from "@oppy/omniv2-ui";
import { useMemo, useRef } from "react";
import { jsx } from "react/jsx-runtime";
function ChartBar({
  stats
}) {
  const hasAnimatedRef = useRef(false);
  const labels = useMemo(
    () => Array.from(stats.keys()).map(
      (value) => DeliverabilityChartBarStatusTranslation[value]
    ),
    [stats]
  );
  const data = useMemo(() => Array.from(stats.values()), [stats]);
  return /* @__PURE__ */ jsx(
    Chart,
    {
      "data-testid": "chart-bar",
      type: "bar",
      className: "m-auto size-full h-[300px] py-4",
      data: {
        labels,
        datasets: [
          {
            backgroundColor: [
              "#56CA00",
              "#3BC0B0",
              "#FFB400",
              "#FFB40080",
              "#FF4C51"
            ],
            borderRadius: 5,
            borderSkipped: false,
            data
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false }
        },
        animation: hasAnimatedRef.current ? false : {
          duration: 1e3,
          onComplete: () => {
            hasAnimatedRef.current = true;
          }
        },
        maintainAspectRatio: false,
        scales: {
          y: {
            display: false,
            beginAtZero: true,
            grid: {
              display: false
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                weight: 600,
                color: "#79767e"
              }
            }
          }
        }
      }
    }
  );
}

// src/lib/components/chart-bar/deliverabilty-status.mapper.ts
function getChartBarstatus(value) {
  switch (value) {
    case "MESSAGE_OPENED" /* MESSAGE_OPENED */:
      return "READ" /* READ */;
    case "RECEIVED" /* RECEIVED */:
      return "RECEIVED" /* RECEIVED */;
    case "MESSAGE_SENT_TO_USER" /* MESSAGE_SENT_TO_USER */:
      return "SENT" /* SENT */;
    case "REFUSED_BY_OPERATOR" /* REFUSED_BY_OPERATOR */:
      return "MNC" /* MNC */;
    case "DELAY_EXPIRED" /* DELAY_EXPIRED */:
    case "UNREACHABLE" /* UNREACHABLE */:
    case "FAILURE" /* FAILURE */:
      return "ERROR" /* ERROR */;
    case "START_COMMUNICATION" /* START_COMMUNICATION */:
    case "IS_TYPING" /* IS_TYPING */:
    case "RESPONSE_RECEIVED" /* RESPONSE_RECEIVED */:
    case "PROCESSING" /* PROCESSING */:
    case "OPT_OUT" /* OPT_OUT */:
    case "END_OF_CONVERSATION" /* END_OF_CONVERSATION */:
    case "UNKNOWN" /* UNKNOWN */:
    default:
      return void 0;
  }
}
function statisticsListToChartData(statusList) {
  if (!statusList?.broadcastResultStatusStatistics?.length) {
    return /* @__PURE__ */ new Map();
  }
  const map = /* @__PURE__ */ new Map([
    ["READ" /* READ */, 0],
    ["RECEIVED" /* RECEIVED */, 0],
    ["SENT" /* SENT */, 0],
    ["MNC" /* MNC */, 0],
    ["ERROR" /* ERROR */, 0]
  ]);
  statusList?.broadcastResultStatusStatistics.forEach(({ label, count }) => {
    const val = getChartBarstatus(label);
    if (val) {
      map.set(val, map.get(val) || 0 + count);
    }
  });
  return map;
}
var colorByChartBarStatus = /* @__PURE__ */ new Map([
  ["READ" /* READ */, "#56CA00"],
  ["RECEIVED" /* RECEIVED */, "#3BC0B0"],
  ["SENT" /* SENT */, "#FFB400"],
  ["MNC" /* MNC */, "#FFB40080"],
  ["ERROR" /* ERROR */, "#FF4C51"],
  [void 0, "#EAEBED"]
]);

// src/lib/components/form-section/action-form.tsx
import { ActionLink, Button } from "@oppy/omniv2-ui";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function ActionForm({
  isSubmitting,
  submitLabel = "Sauvegarder"
}) {
  return /* @__PURE__ */ jsxs("div", { className: "mb-2 flex flex-row-reverse gap-3", children: [
    /* @__PURE__ */ jsx2(ActionLink, { disabled: isSubmitting, variant: "secondary", to: "..", children: "Annuler" }),
    /* @__PURE__ */ jsx2(Button, { type: "submit", variant: "primary", isLoading: isSubmitting, children: submitLabel })
  ] });
}

// src/lib/core/store/rcs-wizard-form/rcs-wizard-form-atom.ts
import { atom } from "jotai";

// src/lib/core/store/rcs-wizard-form/rcs-wizard-form.constants.ts
var INITIAL_NODES = [
  {
    id: "startNode",
    position: { x: 0, y: 0 },
    data: { label: "D\xE9but" },
    type: "startNode",
    deletable: false
  },
  {
    id: "stopMessageNode",
    position: { x: -200, y: 300 },
    data: {
      stopMessage: "Nous avons bien pris en compte votre souhait de ne plus recevoir de message"
    },
    type: "stopMessage",
    deletable: false
  }
];
var INITIAL_EDGES = [
  {
    id: "edge-startNode-stopMessageNode",
    source: "startNode",
    sourceHandle: "a",
    target: "stopMessageNode",
    targetHandle: "a",
    type: "unknown",
    deletable: false
  }
];
var INITIAL_VALUES = {
  name: "",
  description: "",
  flowDataJson: {
    nodes: INITIAL_NODES,
    edges: INITIAL_EDGES
  }
};

// src/lib/core/store/rcs-wizard-form/rcs-wizard-form-atom.ts
var rcsAtom = atom(INITIAL_VALUES);
var generalRcsAtom = atom(
  (get) => {
    const { name, description } = get(rcsAtom);
    return { name, description };
  },
  (get, set, { name, description }) => {
    const currentRcsAtom = get(rcsAtom);
    set(rcsAtom, {
      ...currentRcsAtom,
      name,
      description
    });
  }
);
var flowDataJsonAtom = atom(
  (get) => get(rcsAtom).flowDataJson,
  (get, set, flowDataJson) => {
    const currentRcsAtom = get(rcsAtom);
    set(rcsAtom, {
      ...currentRcsAtom,
      flowDataJson
    });
  }
);

// src/lib/core/store/rcs-visualizer-state-machine/rcs-visualizer-state-machine.ts
import { atom as atom2, useAtom } from "jotai";
import { atomWithMachine } from "jotai-xstate";
import { assign, createMachine } from "xstate";

// src/lib/core/store/rcs-visualizer-state-machine/rcs-visualizer-state-machine.constants.ts
var INITIAL_DELAY_DURATION_IN_MILLISECONDS = 1e3;
var REPLY_DELAY_DURATION_IN_MILLISECONDS = 1e3;
var ON_SENT_DELAY_DURATION_IN_MILLISECONDS = 2e3;
var STOP_MESSAGE = "stop";
var INITIAL_CONVERSATIONS = [
  { nodeId: "startNode", reply: null }
];

// src/lib/core/store/rcs-visualizer-state-machine/utils/get-last-conversation.ts
function getLastConversation(conversations) {
  return conversations.findLast((conversation) => !!conversation.reply);
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/add-node.ts
function addNode(conversations, flow) {
  const { edges } = flow;
  const conversation = getLastConversation(conversations);
  if (!conversation) return conversations;
  const { nodeId, reply } = conversation;
  if (reply?.label.toLocaleLowerCase() === STOP_MESSAGE) {
    return [...conversations, { nodeId: "stopMessageNode", reply: null }];
  }
  const conversationEdge = edges.find(
    (edge) => reply?.postbackData && edge.source.startsWith(nodeId) && edge.sourceHandle?.startsWith(reply.postbackData)
  );
  if (!conversationEdge || !conversationEdge.target) {
    return conversations;
  }
  return [...conversations, { nodeId: conversationEdge.target, reply: null }];
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/edges/custom-smooth-step-edge.tsx
import { SmoothStepEdge } from "@xyflow/react";
import { jsx as jsx3 } from "react/jsx-runtime";
function CustomEdge(props) {
  return /* @__PURE__ */ jsx3(SmoothStepEdge, { ...props, markerEnd: "arrow", style: { strokeWidth: 2 } });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/edges/sent-event-edge.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function SentEventEdge(props) {
  return /* @__PURE__ */ jsx4(CustomEdge, { ...props, markerEnd: "arrow", style: { strokeWidth: 2 } });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/carousel-node.tsx
import { cn as cn5, Paginator, PaginatorTemplateEnum } from "@oppy/omniv2-ui";
import { useState as useState6 } from "react";

// src/lib/components/link/create-link.tsx
import { ActionLink as ActionLink2, faPlusCircle, Icon } from "@oppy/omniv2-ui";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
function CreateLink({ label, to }) {
  return /* @__PURE__ */ jsxs2(ActionLink2, { variant: "text", to, children: [
    /* @__PURE__ */ jsx5(Icon, { icon: faPlusCircle }),
    " ",
    label
  ] });
}

// src/lib/components/rcs-dashboard-statistics-menu/rcs-dashboard-statistics-menu.tsx
import { useCallback, useMemo as useMemo2 } from "react";
import { Outlet, useParams } from "react-router-dom";

// src/lib/components/rcs-dashboard-statistics-menu/statistics-menu-nav-link.tsx
import { ToggleButton } from "@oppy/omniv2-ui";
import { NavLink } from "react-router-dom";
import { jsx as jsx6 } from "react/jsx-runtime";
function StatisticsMenuNavLink({ to, name }) {
  return /* @__PURE__ */ jsx6(NavLink, { to, children: ({ isActive }) => /* @__PURE__ */ jsx6(ToggleButton, { selected: isActive, defaultVariant: "outlined", children: name }) });
}

// src/lib/components/rcs-dashboard-statistics-menu/rcs-dashboard-statistics-menu.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
function RCSDashboardStatisticsMenu() {
  const { entityId, campaignId = "" } = useParams();
  const toEntity = useCallback(
    (to) => entityId ? `/${entityId}${to}` : to,
    [entityId]
  );
  const menuLinks = useMemo2(
    () => [
      {
        name: "Tableau de bord",
        to: toEntity(`/rcs/campaigns/${campaignId}/dashboard`)
      },
      {
        name: "D\xE9liverabilit\xE9",
        to: toEntity(`/rcs/campaigns/${campaignId}/deliverability`)
      },
      {
        name: "Engagement",
        to: toEntity(`/rcs/campaigns/${campaignId}/engagement`)
      },
      {
        name: "D\xE9sinscription",
        to: toEntity(`/rcs/campaigns/${campaignId}/unsubscription`)
      }
    ],
    [campaignId, toEntity]
  );
  return /* @__PURE__ */ jsxs3(Fragment, { children: [
    /* @__PURE__ */ jsx7("div", { className: "mb-4 grid w-fit grid-flow-col gap-3", children: menuLinks.map(({ name, to }) => /* @__PURE__ */ jsx7(StatisticsMenuNavLink, { to, name }, name)) }),
    /* @__PURE__ */ jsx7(Outlet, {})
  ] });
}

// src/lib/components/rich-card/rich-card-image.tsx
import {
  cn
} from "@oppy/omniv2-ui";
import { useMemo as useMemo3, useRef as useRef2 } from "react";

// src/lib/components/rich-card/rich-card-node.constant.tsx
var PICTURE_TYPE_UPLOAD = "upload";
var PICTURE_TYPE_METADATA = "metadata";
var DEFAULT_IMAGE_RICH_CARD = "https://oppy-omniapi-production.s3.eu-west-3.amazonaws.com/66758e163420a4e5c55b2a55/metadata/touchnpay/favicon.png";

// src/lib/components/rich-card/rich-card-image.tsx
import { Fragment as Fragment2, jsx as jsx8 } from "react/jsx-runtime";
function RichCardImage({
  pictureSource,
  cardHeight = "MEDIUM" /* MEDIUM */,
  cardOrientation = "VERTICAL" /* VERTICAL */
}) {
  const picture = pictureSource?.picture ?? null;
  const imageRef = useRef2(null);
  const onImageUrlError = () => {
    if (imageRef.current) {
      imageRef.current.src = DEFAULT_IMAGE_RICH_CARD;
    }
  };
  const imgClassName = useMemo3(() => {
    const HEIGHT_BY_CARD = {
      ["SHORT" /* SHORT */]: "h-[80px]",
      ["MEDIUM" /* MEDIUM */]: "h-[112px]",
      ["TALL" /* TALL */]: "h-[168px]"
    };
    return cn(
      "relative flex items-center w-full justify-center overflow-hidden shrink-0 h-auto",
      cardOrientation === "HORIZONTAL" /* HORIZONTAL */ ? "max-w-[33%] min-h-[112px]" : `max-w-full ${HEIGHT_BY_CARD[cardHeight]}`
    );
  }, [cardHeight, cardOrientation]);
  return /* @__PURE__ */ jsx8(Fragment2, { children: pictureSource?.pictureType === PICTURE_TYPE_METADATA ? /* @__PURE__ */ jsx8("div", { className: imgClassName, children: /* @__PURE__ */ jsx8(
    "img",
    {
      ref: imageRef,
      className: "size-full object-cover",
      src: pictureSource?.picture?.url || DEFAULT_IMAGE_RICH_CARD,
      onError: onImageUrlError,
      alt: "Rich card image"
    }
  ) }) : picture?.src && /* @__PURE__ */ jsx8("div", { className: imgClassName, children: /* @__PURE__ */ jsx8(
    "img",
    {
      className: "size-full object-cover",
      src: picture?.src,
      alt: picture?.name
    }
  ) }) });
}

// src/lib/components/visualizer/header-visualizer.tsx
import {
  faArrowLeft,
  faEllipsis,
  faMagnifyingGlass,
  faShield,
  Icon as Icon2
} from "@oppy/omniv2-ui";
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
function HeaderVisualizer() {
  return /* @__PURE__ */ jsxs4("div", { className: "flex w-full flex-1 flex-col items-center", children: [
    /* @__PURE__ */ jsx9("span", { className: "h-5 w-32 rounded-b-xl border border-gray-50 bg-gray-50" }),
    /* @__PURE__ */ jsxs4("div", { className: "flex h-[40px] w-full items-center border-8", children: [
      /* @__PURE__ */ jsx9(Icon2, { icon: faArrowLeft, className: "size-4 text-gray-500" }),
      /* @__PURE__ */ jsx9("p", { className: "ml-2 text-gray-500", children: "Sender" }),
      /* @__PURE__ */ jsxs4("div", { className: "relative flex size-full items-center justify-end gap-3", children: [
        /* @__PURE__ */ jsx9(Icon2, { icon: faShield, className: "size-4 text-gray-500" }),
        /* @__PURE__ */ jsx9(Icon2, { icon: faMagnifyingGlass, className: "size-4 text-gray-500" }),
        /* @__PURE__ */ jsx9(Icon2, { icon: faEllipsis, className: "size-4 text-gray-500" })
      ] })
    ] })
  ] });
}

// src/lib/components/visualizer/base-visualizer.tsx
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
function BaseVisualizer({ children }) {
  return /* @__PURE__ */ jsxs5("div", { className: "relative flex h-full max-h-[calc(100%_-_32px)] w-[300px] flex-col items-center rounded-3xl border-[10px] border-gray-50 bg-gray-200 shadow", children: [
    /* @__PURE__ */ jsx10(HeaderVisualizer, {}),
    children
  ] });
}

// src/lib/components/visualizer/send-message-visualizer.tsx
import { useCallback as useCallback4 } from "react";

// src/lib/components/visualizer/components/fake-action-buttons.tsx
import { faCamera, faPlus } from "@oppy/omniv2-ui";

// src/lib/components/visualizer/components/fake-icon.tsx
import { Icon as Icon3 } from "@oppy/omniv2-ui";
import { jsx as jsx11 } from "react/jsx-runtime";
function FakeIcon({ icon }) {
  return /* @__PURE__ */ jsx11("div", { className: "relative flex aspect-square h-[30px] min-w-[30px] shrink-0 items-center justify-center rounded-full bg-gray-200 py-1", children: /* @__PURE__ */ jsx11(Icon3, { icon, className: "size-4" }) });
}

// src/lib/components/visualizer/components/fake-action-buttons.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
function FakeActionButtons() {
  return /* @__PURE__ */ jsxs6("div", { className: "flex flex-row items-end gap-1", children: [
    /* @__PURE__ */ jsx12(FakeIcon, { icon: faPlus }),
    /* @__PURE__ */ jsx12(FakeIcon, { icon: faCamera })
  ] });
}

// src/lib/components/visualizer/components/mobile-keyboard.tsx
import "react-simple-keyboard/build/css/index.css";
import Keyboard from "react-simple-keyboard";
import { jsx as jsx13 } from "react/jsx-runtime";
function MobileKeyboard({
  layoutName,
  keyboardRef,
  onKeyPress,
  keyboardOptions: keyboardOptions2
}) {
  return /* @__PURE__ */ jsx13(
    Keyboard,
    {
      ref: keyboardRef,
      layoutName,
      onKeyPress,
      ...keyboardOptions2
    }
  );
}

// src/lib/components/visualizer/components/textarea-input.tsx
import { forwardRef } from "react";
import { jsx as jsx14 } from "react/jsx-runtime";
var TextAreaInput = forwardRef(
  ({
    value,
    onKeyUp,
    onKeyDown,
    onClick,
    onChange = () => {
    },
    disabled = false
  }, ref) => /* @__PURE__ */ jsx14(
    "textarea",
    {
      ref,
      value,
      onKeyUp,
      onKeyDown,
      onClick,
      onChange,
      placeholder: "Message",
      disabled,
      className: "max-h-[100px] min-h-[30px] w-full resize-none overflow-hidden rounded-[15px] bg-gray-200 px-3 py-1 text-sm focus:outline-none disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-400",
      rows: 1
    }
  )
);
TextAreaInput.displayName = "TextAreaInput";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/hooks/use-keyboard-utils.ts
import { useCallback as useCallback2 } from "react";
function useKeyboardUtils() {
  const isPrintableKey = useCallback2((event) => {
    const { key } = event;
    const nonPrintableKeys = /* @__PURE__ */ new Set([
      "Enter",
      "Shift",
      "Control",
      "Alt",
      "Tab",
      "CapsLock",
      "Meta",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "Backspace",
      "Escape",
      "Esc",
      "Delete",
      "Home",
      "End",
      "PageUp",
      "PageDown",
      "F1",
      "F2",
      "F3",
      "F4",
      "F5",
      "F6",
      "F7",
      "F8",
      "F9",
      "F10",
      "F11",
      "F12",
      "ContextMenu",
      "NumLock",
      "ScrollLock"
    ]);
    if (nonPrintableKeys.has(key)) return false;
    if (key.length === 1) return key.charCodeAt(0) >= 32;
    return true;
  }, []);
  const isEnterKey = useCallback2(
    (event) => event.key === "Enter",
    []
  );
  return { isPrintableKey, isEnterKey };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/hooks/use-submit-free-text.ts
function useSubmitFreeText() {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const submitFreeText = (text) => {
    const reply = {
      type: "freeTextResponse" /* FREETEXTRESPONSE */,
      label: text,
      renderable: true
    };
    sendEvent({ type: "freeText" /* FREETEXT */, reply });
  };
  return {
    submitFreeText
  };
}

// src/lib/components/visualizer/hooks/use-keyboard-input.ts
import { useCallback as useCallback3, useEffect, useRef as useRef3, useState } from "react";
var keyboardOptions = {
  layout: {
    default: [
      "a z e r t y u i o p",
      "q s d f g h j k l m",
      "{capslock} w x c v b n {backspace}",
      "{numbers} {space} {ent}"
    ],
    shift: [
      "A Z E R T Y U I O P",
      "Q S D F G H J K L M",
      "{capslock} W X C V B N {backspace}",
      "{numbers} {space} {ent}"
    ],
    numbers: ["1 2 3", "4 5 6", "7 8 9", "{abc} 0 {backspace}"]
  },
  display: {
    "{space}": "\u2003\u2003\u2003\u2003\u2003",
    "{numbers}": "123",
    "{ent}": "retour",
    "{escape}": "esc \u238B",
    "{tab}": "tab \u21E5",
    "{backspace}": "\u232B",
    "{capslock}": "\u2006\u2006\u2006\u2006\u21EA\u2006\u2006\u2006\u2006",
    "{shift}": "\u2006\u2006\u2006\u2006\u21E7\u2006\u2006\u2006\u2006",
    "{controlleft}": "ctrl \u2303",
    "{controlright}": "ctrl \u2303",
    "{altleft}": "alt \u2325",
    "{altright}": "alt \u2325",
    "{metaleft}": "cmd \u2318",
    "{metaright}": "cmd \u2318",
    "{abc}": "ABC"
  },
  theme: "hg-theme-default hg-layout-default keyboard-theme",
  buttonTheme: [
    {
      class: "!bg-gray-300",
      buttons: "{numbers} {capslock} {backspace} {ent}"
    }
  ],
  syncInstanceInputs: true,
  inputPattern: /^.+$/,
  useMouseEvents: true
};
function useKeyboardInput() {
  const [isTyping, setIsTyping] = useState(false);
  const [input, setInput] = useState("");
  const [layoutName, setLayoutName] = useState("default");
  const [keyboardState, setKeyboardState] = useState({
    isNumbersActive: false,
    isShiftLocked: false
  });
  const { submitFreeText } = useSubmitFreeText();
  const { isEnterKey, isPrintableKey } = useKeyboardUtils();
  const textareaRef = useRef3(null);
  const keyboardContainerRef = useRef3(null);
  const messageContainerRef = useRef3(null);
  const keyboardRef = useRef3(null);
  useEffect(() => {
    if (keyboardRef.current) {
      keyboardRef.current.setInput("");
    }
  }, [keyboardRef, layoutName]);
  useEffect(() => {
    const closeKeyboardOnOutsideClick = (event) => {
      if (!isTyping) return;
      const targetNode = event instanceof MouseEvent ? event.target : event.touches[0]?.target;
      if (!targetNode) return;
      const isInsideMessageContainer = messageContainerRef.current?.contains(targetNode);
      const isInsideKeyboardContainer = keyboardContainerRef.current?.contains(targetNode);
      if (!isInsideMessageContainer && !isInsideKeyboardContainer) {
        setIsTyping(false);
      }
    };
    document.addEventListener("mousedown", closeKeyboardOnOutsideClick);
    document.addEventListener("touchstart", closeKeyboardOnOutsideClick, {
      passive: true
    });
    return () => {
      document.removeEventListener("mousedown", closeKeyboardOnOutsideClick);
      document.removeEventListener("touchstart", closeKeyboardOnOutsideClick);
    };
  }, [isTyping]);
  useEffect(() => {
    if (isTyping && textareaRef.current) {
      textareaRef.current.focus();
      const length = textareaRef.current.value.length;
      textareaRef.current.selectionStart = length;
      textareaRef.current.selectionEnd = length;
    }
  }, [isTyping]);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        100,
        textareaRef.current.scrollHeight
      )}px`;
    }
  }, [input]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Shift") {
        setLayoutName("shift");
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === "Shift") {
        setLayoutName("default");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);
  const activateKeyboard = () => {
    setIsTyping(true);
  };
  const appendText = (text) => {
    setInput((prev) => prev + text);
  };
  const handleSelectedTextDeletion = () => {
    if (!textareaRef.current) return false;
    const { selectionStart, selectionEnd } = textareaRef.current;
    if (selectionStart === selectionEnd) return false;
    setInput(
      (prev) => prev.substring(0, selectionStart) + prev.substring(selectionEnd)
    );
    return true;
  };
  const submitMessage = useCallback3(() => {
    if (input.trim()) {
      submitFreeText(input);
    }
    setInput("");
    setIsTyping(false);
  }, [input, submitFreeText]);
  const handlePhysicalKeyPress = (event) => {
    if (isEnterKey(event)) {
      submitMessage();
      event.preventDefault();
    } else if (event.key === "Backspace") {
      if (!handleSelectedTextDeletion()) {
        setInput((prev) => prev.slice(0, -1));
      }
      event.preventDefault();
    } else if (event.ctrlKey && event.key.toLowerCase() === "a") {
      textareaRef.current?.select();
      event.preventDefault();
    } else if (isPrintableKey(event)) {
      appendText(event.key);
      event.preventDefault();
    }
  };
  const handlePhysicalKeyDown = (event) => {
    const shouldPreventDefault = event.key === "Backspace" || isEnterKey(event) || event.ctrlKey && event.key.toLowerCase() === "a" || isPrintableKey(event);
    if (shouldPreventDefault) {
      event.preventDefault();
    }
  };
  const handleKeyPress = useCallback3(
    (button) => {
      if (keyboardRef.current) {
        keyboardRef.current.addButtonTheme(button, "animate-key-press");
        setTimeout(() => {
          if (keyboardRef.current) {
            keyboardRef.current.removeButtonTheme(button, "animate-key-press");
          }
        }, 300);
        keyboardRef.current.setInput("");
      }
      if (button === "{ent}") {
        submitMessage();
      } else if (button === "{shift}") {
        setKeyboardState((prev) => {
          const newShiftLocked = !prev.isShiftLocked;
          setLayoutName(newShiftLocked ? "shift" : "default");
          setTimeout(() => {
            const shiftButton = document.querySelector(".hg-button-shift");
            if (shiftButton) {
              if (newShiftLocked) {
                shiftButton.classList.add("shift-locked");
              } else {
                shiftButton.classList.remove("shift-locked");
              }
            }
          }, 10);
          return {
            ...prev,
            isShiftLocked: newShiftLocked
          };
        });
      } else if (button === "{space}") {
        appendText(" ");
      } else if (button === "{numbers}") {
        setLayoutName("numbers");
        setKeyboardState((prev) => ({
          ...prev,
          isNumbersActive: true
        }));
      } else if (button === "{abc}") {
        setLayoutName("default");
        setKeyboardState((prev) => ({
          ...prev,
          isNumbersActive: false
        }));
      } else if (button === "{capslock}") {
        setLayoutName(layoutName === "shift" ? "default" : "shift");
      } else if (button === "{backspace}") {
        if (!handleSelectedTextDeletion()) {
          setInput((prev) => prev.slice(0, -1));
        }
      } else if (!button.startsWith("{")) {
        appendText(button);
      }
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    },
    [layoutName, submitMessage]
  );
  return {
    isTyping,
    input,
    keyboardState,
    layoutName,
    keyboardRef,
    textareaRef,
    keyboardContainerRef,
    messageContainerRef,
    activateKeyboard,
    handlePhysicalKeyPress,
    handlePhysicalKeyDown,
    handleKeyPress,
    keyboardOptions
  };
}

// src/lib/components/visualizer/send-message-visualizer.tsx
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
function SendMessageVisualizer({
  isAnswering = false,
  isInteractive = true
}) {
  const {
    isTyping,
    input,
    layoutName,
    keyboardRef,
    textareaRef,
    keyboardContainerRef,
    messageContainerRef,
    keyboardOptions: keyboardOptions2,
    activateKeyboard,
    handlePhysicalKeyPress,
    handlePhysicalKeyDown,
    handleKeyPress
  } = useKeyboardInput();
  const dummyFn = useCallback4(() => {
  }, []);
  const handleClick = isInteractive ? activateKeyboard : dummyFn;
  const handleKeyUp = isInteractive ? handlePhysicalKeyPress : dummyFn;
  const handleKeyDown = isInteractive ? handlePhysicalKeyDown : dummyFn;
  return /* @__PURE__ */ jsxs7("div", { className: "flex flex-col", children: [
    isAnswering && /* @__PURE__ */ jsx15("p", { className: "text-xs", children: "En train de vous \xE9crire..." }),
    /* @__PURE__ */ jsxs7("div", { className: "flex w-full gap-1 py-1", children: [
      /* @__PURE__ */ jsx15("div", { className: "flex shrink-0 flex-row gap-1", children: /* @__PURE__ */ jsx15(FakeActionButtons, {}) }),
      /* @__PURE__ */ jsx15(
        "div",
        {
          ref: messageContainerRef,
          className: "relative flex w-full grow items-center",
          children: /* @__PURE__ */ jsx15(
            TextAreaInput,
            {
              ref: textareaRef,
              value: input,
              onKeyUp: handleKeyUp,
              onKeyDown: handleKeyDown,
              onClick: handleClick,
              disabled: !isInteractive
            }
          )
        }
      )
    ] }),
    isInteractive && isTyping && /* @__PURE__ */ jsx15("div", { ref: keyboardContainerRef, children: /* @__PURE__ */ jsx15(
      MobileKeyboard,
      {
        layoutName,
        keyboardRef,
        onKeyPress: handleKeyPress,
        keyboardOptions: keyboardOptions2
      }
    ) })
  ] });
}

// src/lib/components/visualizer/message-visualizer.tsx
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
function MessageVisualizer({
  children,
  isAnswering,
  isInteractive
}) {
  return /* @__PURE__ */ jsx16(BaseVisualizer, { children: /* @__PURE__ */ jsxs8("div", { className: "relative flex size-full h-screen max-h-[calc(100%_-_60px)] flex-col justify-between gap-y-3 rounded-b-xl bg-white px-3 pb-3", children: [
    children,
    /* @__PURE__ */ jsx16(
      SendMessageVisualizer,
      {
        isAnswering,
        isInteractive
      }
    )
  ] }) });
}

// src/lib/components/visualizer/preview-card-visualizer.tsx
import { Heading2 } from "@oppy/omniv2-ui";
import { jsx as jsx17, jsxs as jsxs9 } from "react/jsx-runtime";
function PreviewCardVisualizer({ children }) {
  return /* @__PURE__ */ jsxs9("div", { className: "sticky top-0 h-[600px] space-y-2 pt-2", children: [
    /* @__PURE__ */ jsx17(Heading2, { children: "Pr\xE9visualisation" }),
    /* @__PURE__ */ jsx17(MessageVisualizer, { isInteractive: false, children: /* @__PURE__ */ jsx17("div", { className: "space-y-2 overflow-y-auto scroll-smooth pt-2 no-scrollbar", children }) })
  ] });
}

// src/lib/components/visualizer/text-icon-visualizer.tsx
import { Icon as Icon4 } from "@oppy/omniv2-ui";
import { jsx as jsx18, jsxs as jsxs10 } from "react/jsx-runtime";
function TextIconVisualizer({ icon, text, color }) {
  return /* @__PURE__ */ jsxs10("div", { className: "flex flex-col items-center justify-center", children: [
    /* @__PURE__ */ jsx18(Icon4, { icon, className: "size-5 text-primary", style: { color } }),
    /* @__PURE__ */ jsx18("span", { className: "text-xs text-primary", style: { color }, children: text })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/drag-n-drop/drag-n-drop.constants.ts
var NO_DROPPABLE_ID = "no-droppable";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/drag-n-drop/no-droppable.tsx
import { useDroppable } from "@dnd-kit/core";
import { cn as cn2 } from "@oppy/omniv2-ui";
import { useId } from "react";
import { jsx as jsx19 } from "react/jsx-runtime";
function NoDroppable({ children, className }) {
  const uniqueId = useId();
  const { setNodeRef } = useDroppable({
    id: `${NO_DROPPABLE_ID}-${uniqueId}`
  });
  return /* @__PURE__ */ jsx19("div", { className: cn2("wrapper__no-droppable", className), ref: setNodeRef, children });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/actions-handle.tsx
import { Fragment as Fragment3 } from "react/jsx-runtime";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-handle.tsx
import { faCalendarAlt, Icon as Icon9 } from "@oppy/omniv2-ui";
import { Position as Position2 } from "@xyflow/react";
import { useMemo as useMemo5 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/base-action-handle.tsx
import { Position } from "@xyflow/react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/connection-limit-handle.tsx
import { useNodeConnections } from "@xyflow/react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/base-handle.tsx
import { Handle } from "@xyflow/react";
import { jsx as jsx20 } from "react/jsx-runtime";
function BaseHandle({
  isConnectable = true,
  ...props
}) {
  return /* @__PURE__ */ jsx20(
    Handle,
    {
      ...props,
      isConnectable,
      className: "size-[10px]"
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/connection-limit-handle.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var HANDLE_TYPE = "source";
function ConnectionLimitHandle({
  connectionLimit = 1,
  ...props
}) {
  const connections = useNodeConnections({
    handleId: props.id ?? void 0,
    handleType: HANDLE_TYPE
  });
  return /* @__PURE__ */ jsx21(
    BaseHandle,
    {
      ...props,
      type: HANDLE_TYPE,
      isConnectable: connections.length < connectionLimit
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/base-action-handle.tsx
import { jsx as jsx22, jsxs as jsxs11 } from "react/jsx-runtime";
function BaseActionHandle({
  canHandle,
  children,
  postbackData,
  position = Position.Right,
  ...props
}) {
  return /* @__PURE__ */ jsxs11("div", { className: "relative w-full", children: [
    /* @__PURE__ */ jsx22("div", { children }),
    canHandle && /* @__PURE__ */ jsx22(
      ConnectionLimitHandle,
      {
        ...props,
        id: postbackData,
        position
      }
    )
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/create-calendar-event-action-handle.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var BASE_URL = "https://calendar.google.com/calendar/render";
var ACTION_TYPE = "TEMPLATE";
var formatDateISOStringToGoogleCalendarDate = function(dateISOString) {
  return dateISOString.replace(/-|:|\.\d+/g, "");
};
var generateGoogleCalendarEventUrl = function({
  title,
  description,
  startTime,
  endTime
}) {
  if (!startTime) throw new ReferenceError("Unvailable startTime date");
  if (!endTime) throw new ReferenceError("Unvailable endTime date");
  const startTimeToGoogleCalendarDate = formatDateISOStringToGoogleCalendarDate(
    startTime.toString()
  );
  const endTimeToGoogleCalendarDate = formatDateISOStringToGoogleCalendarDate(
    endTime.toString()
  );
  const urlSearchParams = new URLSearchParams({
    action: ACTION_TYPE,
    text: title,
    details: description,
    dates: `${startTimeToGoogleCalendarDate}/${endTimeToGoogleCalendarDate}`
  });
  return `${BASE_URL}?${urlSearchParams.toString()}`;
};
function CreateCalendarEventActionHandle({
  data,
  children,
  className
}) {
  const { startTime, endTime } = data;
  const calendarUrl = (startTime ? !isNaN(new Date(startTime).getTime()) : false) && (endTime ? !isNaN(new Date(endTime).getTime()) : false) ? generateGoogleCalendarEventUrl(data) : void 0;
  return /* @__PURE__ */ jsx23("a", { href: calendarUrl, target: "_blank", className, children });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/phone-action-handle.tsx
import { faPhone, Icon as Icon5 } from "@oppy/omniv2-ui";
import { jsx as jsx24, jsxs as jsxs12 } from "react/jsx-runtime";
function PhoneActionHandle({ label, number, className }) {
  return /* @__PURE__ */ jsx24("p", { className: "text-center", children: /* @__PURE__ */ jsxs12("a", { href: `tel:${number}`, className, children: [
    /* @__PURE__ */ jsx24(Icon5, { className: "mr-2", icon: faPhone }),
    label
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/reply-action-handle.tsx
import { faReply, Icon as Icon6 } from "@oppy/omniv2-ui";
import { jsx as jsx25, jsxs as jsxs13 } from "react/jsx-runtime";
function ReplyActionHandle({ label }) {
  return /* @__PURE__ */ jsx25("p", { className: "text-center", children: /* @__PURE__ */ jsxs13("a", { target: "_blank", className: "text-sky-700 underline", children: [
    /* @__PURE__ */ jsx25(Icon6, { className: "mr-2", icon: faReply }),
    label
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/url-action-handle.tsx
import { faGlobeEurope, Icon as Icon7 } from "@oppy/omniv2-ui";
import { jsx as jsx26, jsxs as jsxs14 } from "react/jsx-runtime";
function UrlActionHandle({ href, label, className }) {
  return /* @__PURE__ */ jsx26("p", { className: "text-center", children: /* @__PURE__ */ jsxs14("a", { href, target: "_blank", className, children: [
    /* @__PURE__ */ jsx26(Icon7, { className: "mr-2", icon: faGlobeEurope }),
    label
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-type/view-location-action-handle.tsx
import { faLocationDot, Icon as Icon8 } from "@oppy/omniv2-ui";
import { useCallback as useCallback5, useMemo as useMemo4 } from "react";
import { jsx as jsx27, jsxs as jsxs15 } from "react/jsx-runtime";
var BASE_URL2 = "https://www.google.com/maps/place/";
function ViewLocationActionHandle({ data, className }) {
  const { label, location } = data;
  const generateGoogleMapsUrl = useCallback5(
    (location2) => {
      if (!location2?.latLong?.latitude || !location2?.latLong?.longitude)
        return void 0;
      const {
        latLong: { latitude, longitude }
      } = location2;
      return `${BASE_URL2}${latitude},${longitude}`;
    },
    []
  );
  const mapUrl = useMemo4(
    () => generateGoogleMapsUrl(location),
    [generateGoogleMapsUrl, location]
  );
  return /* @__PURE__ */ jsx27("p", { className: "text-center", children: /* @__PURE__ */ jsxs15("a", { href: mapUrl, target: "_blank", className, children: [
    /* @__PURE__ */ jsx27(Icon8, { className: "mr-2", icon: faLocationDot }),
    label
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/action-handle.tsx
import { jsx as jsx28, jsxs as jsxs16 } from "react/jsx-runtime";
function ActionHandle({
  data,
  position = Position2.Right
}) {
  const { actionType, label, postbackData } = data;
  const canHandle = actionType === "reply";
  const chipClassName = "space-x-2 text-sky-700 underline";
  const actionComponent = useMemo5(() => {
    switch (actionType) {
      case "call":
        return /* @__PURE__ */ jsx28(
          PhoneActionHandle,
          {
            number: data.number,
            label,
            className: chipClassName
          }
        );
      case "createCalendarEventAction":
        return /* @__PURE__ */ jsx28(
          CreateCalendarEventActionHandle,
          {
            data,
            className: "space-x-2 text-sky-700 underline",
            children: /* @__PURE__ */ jsxs16("p", { className: "text-center", children: [
              /* @__PURE__ */ jsx28(Icon9, { className: "mr-2", icon: faCalendarAlt }),
              label
            ] })
          }
        );
      case "viewLocationAction":
        return /* @__PURE__ */ jsx28(ViewLocationActionHandle, { data, className: chipClassName });
      case "url":
        return /* @__PURE__ */ jsx28(
          UrlActionHandle,
          {
            href: data.href,
            label,
            className: chipClassName
          }
        );
      case "reply":
        return /* @__PURE__ */ jsx28(ReplyActionHandle, { label: data.label });
    }
  }, [actionType, data, label]);
  return /* @__PURE__ */ jsx28(
    BaseActionHandle,
    {
      canHandle,
      postbackData,
      style: position === Position2.Right ? { right: "-16px" } : { left: "-16px" },
      position,
      children: actionComponent
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/actions/actions-handle.tsx
import { jsx as jsx29, jsxs as jsxs17 } from "react/jsx-runtime";
function ActionsHandle({ data, position }) {
  return /* @__PURE__ */ jsx29("div", { className: "flex flex-col gap-y-2", children: data.map((action, index) => /* @__PURE__ */ jsxs17(Fragment3, { children: [
    index !== 0 && /* @__PURE__ */ jsx29("hr", { className: "h-px border-0 bg-gray-300" }),
    /* @__PURE__ */ jsx29(
      ActionHandle,
      {
        data: action,
        position
      },
      action.postbackData
    )
  ] }, `action-${index}-${action.label}`)) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/free-text-responses/free-text-responses-handle.tsx
import { Fragment as Fragment7 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/free-text-responses/text-response-handle.tsx
import { Position as Position4 } from "@xyflow/react";
import { Fragment as Fragment6 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/base/content-node.tsx
import { cn as cn3 } from "@oppy/omniv2-ui";
import { jsx as jsx30 } from "react/jsx-runtime";
function ContentNode({
  children,
  type = enums_default.NONE
}) {
  return /* @__PURE__ */ jsx30(
    "div",
    {
      className: cn3(
        "space-y-3 whitespace-pre-line break-words rounded-sm border-gray-400 bg-white p-2 text-xs h-full",
        {
          "rounded-2xl border border-width-2 border-gray-300 text-black px-3": type !== enums_default.NONE
        },
        { "bg-orange-100": type === enums_default.SUGGESTION },
        { "bg-indigo-100": type === enums_default.FREETEXTRESPONSE }
      ),
      children
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/base/enums.ts
var ContentNodeTypeEnum = /* @__PURE__ */ ((ContentNodeTypeEnum2) => {
  ContentNodeTypeEnum2["NONE"] = "none";
  ContentNodeTypeEnum2["SUGGESTION"] = "suggestion";
  ContentNodeTypeEnum2["FREETEXTRESPONSE"] = "freeTextResponse";
  return ContentNodeTypeEnum2;
})(ContentNodeTypeEnum || {});
var enums_default = ContentNodeTypeEnum;

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/base/header-node.tsx
import { faPenToSquare, IconButton } from "@oppy/omniv2-ui";
import { Position as Position3 } from "@xyflow/react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/sent-event-handle.tsx
import { useNodeId, useReactFlow } from "@xyflow/react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/handle-not-connectable.tsx
import { Tooltip } from "@oppy/omniv2-ui";
import { Fragment as Fragment4, jsx as jsx31, jsxs as jsxs18 } from "react/jsx-runtime";
function HandleNoConnectable({ message }) {
  return /* @__PURE__ */ jsxs18(Fragment4, { children: [
    /* @__PURE__ */ jsx31(
      Tooltip,
      {
        target: `#tooltip-handle-not-connectable`,
        position: "top",
        className: "text-xs",
        children: message
      }
    ),
    /* @__PURE__ */ jsx31(
      "div",
      {
        className: "absolute right-[-5px] size-[10px] cursor-auto rounded-full border border-white bg-gray-500",
        id: "tooltip-handle-not-connectable"
      }
    )
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/sent-event-handle.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var isReplyAction = (action) => !!action && typeof action === "object" && "actionType" in action && action.actionType === "reply";
var isNonEmptyArray = (arr) => arr.length > 0;
var hasReplyActionType = (arr) => arr.some(isReplyAction);
var getNodeData = (node, property) => node?.data?.[property];
var validateNodeProperty = (node, property, validator = isNonEmptyArray) => {
  const data = getNodeData(node, property);
  return Array.isArray(data) && validator(data);
};
var checkAllInteractiveElements = (interactiveElements) => Object.values(interactiveElements).every((value) => !value);
var useSentEventHandle = () => {
  const nodeId = useNodeId();
  if (!nodeId) {
    throw new Error(
      "useSentEventHandle must be used inside a node context - no valid nodeId found"
    );
  }
  const reactFlow = useReactFlow();
  const node = reactFlow.getNode(nodeId);
  const interactiveElements = {
    hasReplyActions: validateNodeProperty(node, "actions", hasReplyActionType),
    hasSuggestions: validateNodeProperty(node, "suggestions"),
    hasFreeTextResponses: validateNodeProperty(node, "freeTextResponses")
  };
  const hasNoInteractiveElements = checkAllInteractiveElements(interactiveElements);
  return {
    isConnectable: hasNoInteractiveElements
  };
};
function SentEventHandle({
  ...props
}) {
  const { isConnectable } = useSentEventHandle();
  return isConnectable ? /* @__PURE__ */ jsx32(
    BaseHandle,
    {
      ...props,
      id: ON_SENT_HANDLE_ID,
      type: "source",
      isConnectable
    }
  ) : /* @__PURE__ */ jsx32(HandleNoConnectable, { message: "Ce noeud ne peut pas \xEAtre connect\xE9 car des suggestions, des r\xE9ponses libres ou des r\xE9ponses de type r\xE9ponse sont pr\xE9sentes" });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/base/header-node-title.tsx
import { faCircleCheck, Icon as Icon10, useToastContext } from "@oppy/omniv2-ui";
import { useClipboard } from "@oppy/omniv2-utils";
import { jsx as jsx33, jsxs as jsxs19 } from "react/jsx-runtime";
function HeaderNodeTitle({ nodeId, children }) {
  const { copyToClipboard } = useClipboard();
  const { showMessage } = useToastContext();
  const handleCopyId = (copyFn, value, showMessage2) => async (e) => {
    e.stopPropagation();
    try {
      await copyFn(value);
      showMessage2({
        severity: "success",
        content: () => /* @__PURE__ */ jsxs19("div", { className: "flex w-full items-center justify-between gap-4", children: [
          /* @__PURE__ */ jsx33(Icon10, { icon: faCircleCheck, className: "w-auto" }),
          /* @__PURE__ */ jsx33("span", { className: "w-full", children: "Copi\xE9 dans le presse-papier" })
        ] }),
        closable: false
      });
    } catch {
      showMessage2({
        severity: "error",
        content: "nope",
        closable: false
      });
    }
  };
  return /* @__PURE__ */ jsxs19("div", { className: "flex w-fit flex-col items-start", children: [
    children,
    /* @__PURE__ */ jsxs19(
      "button",
      {
        className: "text-[8px] text-gray-500",
        onClick: handleCopyId(copyToClipboard, nodeId, showMessage),
        type: "button",
        children: [
          "Id : ",
          nodeId
        ]
      }
    )
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/base/header-node.tsx
import { jsx as jsx34, jsxs as jsxs20 } from "react/jsx-runtime";
function HeaderNode({
  children,
  sentHandle = true,
  title,
  onEdit,
  isConnectable,
  nodeId
}) {
  return /* @__PURE__ */ jsxs20("div", { className: "relative flex items-center justify-between rounded-t-md border-b border-gray-400 bg-white p-2", children: [
    /* @__PURE__ */ jsx34(
      BaseHandle,
      {
        id: "a",
        type: "target",
        position: Position3.Left,
        isConnectable
      }
    ),
    /* @__PURE__ */ jsx34(HeaderNodeTitle, { nodeId, children: /* @__PURE__ */ jsx34("span", { children: title }) }),
    /* @__PURE__ */ jsxs20("div", { className: "size-8", children: [
      /* @__PURE__ */ jsx34(
        IconButton,
        {
          iconProps: {
            icon: faPenToSquare
          },
          size: "small",
          spacing: "none",
          rounded: true,
          variant: "secondary",
          onClick: onEdit
        }
      ),
      children
    ] }),
    sentHandle && /* @__PURE__ */ jsx34(SentEventHandle, { position: Position3.Right })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/prefixable-label/prefixable-label.tsx
import { Fragment as Fragment5 } from "react";
import { jsx as jsx35, jsxs as jsxs21 } from "react/jsx-runtime";
function PrefixedLabel({
  prefix,
  label,
  maxLength = 30
}) {
  const shouldTruncate = label && label.length > maxLength;
  const displayLabel = shouldTruncate ? `${label.substring(0, maxLength)}...` : label;
  return /* @__PURE__ */ jsxs21(Fragment5, { children: [
    /* @__PURE__ */ jsx35("span", { className: "font-medium", children: prefix }),
    " ",
    shouldTruncate ? /* @__PURE__ */ jsx35("span", { title: label, className: "cursor-help", children: displayLabel }) : displayLabel
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/free-text-responses/text-response-handle.tsx
import { jsx as jsx36, jsxs as jsxs22 } from "react/jsx-runtime";
function TextResponseHandle({
  prefix,
  matchers,
  position = Position4.Right
}) {
  return /* @__PURE__ */ jsx36(Fragment6, { children: matchers?.map((matcher, index) => /* @__PURE__ */ jsxs22(
    "div",
    {
      className: "relative mb-2 w-full px-2",
      children: [
        /* @__PURE__ */ jsx36(ContentNode, { type: enums_default.FREETEXTRESPONSE, children: /* @__PURE__ */ jsx36(
          PrefixedLabel,
          {
            prefix,
            label: matcher.value,
            maxLength: prefix === "RegEx" ? 15 : 30
          }
        ) }),
        /* @__PURE__ */ jsx36(
          ConnectionLimitHandle,
          {
            id: matcher.postbackData,
            position,
            style: {
              width: "10px",
              height: "10px"
            }
          }
        )
      ]
    },
    `${matcher.postbackData}-${index}`
  )) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/free-text-responses/free-text-response-handle.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var modeConfigs = {
  ["EQUALS_TO" /* EQUALS_TO */]: {
    mode: "EQUALS_TO" /* EQUALS_TO */,
    prefix: "Est \xE9gale"
  },
  ["DIFFERENT_FROM" /* DIFFERENT_FROM */]: {
    mode: "DIFFERENT_FROM" /* DIFFERENT_FROM */,
    prefix: "Diff\xE8rent de"
  },
  ["FREE_RESPONSE" /* FREE_RESPONSE */]: {
    mode: "FREE_RESPONSE" /* FREE_RESPONSE */,
    prefix: "R\xE9ponse libre"
  },
  ["REGEX" /* REGEX */]: {
    mode: "REGEX" /* REGEX */,
    prefix: "Regex"
  }
};
function FreeTextResponseHandle({
  data,
  position
}) {
  const config = modeConfigs[data.mode];
  return /* @__PURE__ */ jsx37(
    TextResponseHandle,
    {
      prefix: config?.prefix,
      matchers: data.matchers,
      position
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/free-text-responses/free-text-responses-handle.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
function FreeTextResponsesHandle({ data, position }) {
  return /* @__PURE__ */ jsx38(Fragment7, { children: data.map((freeTextResponse, freeTextResponseIdx) => /* @__PURE__ */ jsx38(
    FreeTextResponseHandle,
    {
      data: freeTextResponse,
      position
    },
    freeTextResponseIdx
  )) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/suggestions/suggestions-handle.tsx
import { Fragment as Fragment8 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/suggestions/suggestion-handle.tsx
import {
  Badge,
  faCalendarAlt as faCalendarAlt2,
  faLink,
  faLocationDot as faLocationDot2,
  faPhone as faPhone2,
  faReply as faReply2,
  Icon as Icon11
} from "@oppy/omniv2-ui";
import { Position as Position5 } from "@xyflow/react";
import { useMemo as useMemo6 } from "react";
import { jsx as jsx39, jsxs as jsxs23 } from "react/jsx-runtime";
function SuggestionHandle({
  data,
  position = Position5.Right
}) {
  const icon = useMemo6(() => {
    switch (data.actionType) {
      case "call":
        return faPhone2;
      case "url":
        return faLink;
      case "createCalendarEventAction":
        return faCalendarAlt2;
      case "viewLocationAction":
        return faLocationDot2;
      case "reply":
      default:
        return faReply2;
    }
  }, [data]);
  return /* @__PURE__ */ jsxs23("div", { className: "relative mb-2 w-full px-2", children: [
    /* @__PURE__ */ jsx39(ContentNode, { type: enums_default.SUGGESTION, children: /* @__PURE__ */ jsxs23("div", { className: "flex items-center  gap-1", children: [
      /* @__PURE__ */ jsx39(Icon11, { icon }),
      /* @__PURE__ */ jsx39(PrefixedLabel, { prefix: "", label: data.label }),
      typeof data?.stat !== "undefined" && /* @__PURE__ */ jsx39(Badge, { variant: "info", children: data?.stat })
    ] }) }),
    data.actionType === "reply" && /* @__PURE__ */ jsx39(
      ConnectionLimitHandle,
      {
        id: data.postbackData,
        position,
        style: { width: "10px", height: "10px" }
      }
    )
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/handles/suggestions/suggestions-handle.tsx
import { jsx as jsx40 } from "react/jsx-runtime";
function SuggestionsHandle({ data, position }) {
  return /* @__PURE__ */ jsx40(Fragment8, { children: data.map((suggestion) => /* @__PURE__ */ jsx40(
    SuggestionHandle,
    {
      data: suggestion,
      position
    },
    suggestion.postbackData
  )) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/dialog-form.tsx
import { Dialog, Heading2 as Heading27 } from "@oppy/omniv2-ui";
import { useCallback as useCallback12 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/content-form.tsx
import { Button as Button6 } from "@oppy/omniv2-ui";
import { FormProvider, useFieldArray as useFieldArray5, useWatch as useWatch3 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/hooks/use-carousel-form.ts
import { useRef as useRef4, useState as useState2 } from "react";
function useCarouselForm({
  length
}) {
  const [indexCarouselSelected, setIndexCarouselSelected] = useState2(0);
  const refCarousel = useRef4(null);
  const handleRefCarousel = (index) => {
    setIndexCarouselSelected(index);
    if (refCarousel.current !== null) {
      const scrollTo = refCarousel.current.scrollWidth / length;
      const scrollGap = refCarousel.current.offsetWidth - scrollTo;
      refCarousel.current.scrollLeft = scrollTo * index - scrollGap / 2;
    }
  };
  return {
    indexCarouselSelected,
    setIndexCarouselSelected,
    refCarousel,
    handleRefCarousel
  };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/actions-form.tsx
import {
  Button as Button2,
  Card as Card2,
  faPlus as faPlus2,
  faQuestion,
  Heading2 as Heading23,
  Icon as Icon12,
  Select
} from "@oppy/omniv2-ui";
import { generateUniqueIdentifier } from "@oppy/omniv2-utils";
import { useCallback as useCallback7, useMemo as useMemo7 } from "react";
import { useFieldArray } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/hooks/use-disabled-connections.ts
import { useNodeConnections as useNodeConnections2 } from "@xyflow/react";
function useDisabledConnections() {
  const connections = useNodeConnections2({
    handleType: "source",
    handleId: ON_SENT_HANDLE_ID
  });
  return !!connections.length;
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-form.tsx
import { useWatch } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/base-action.tsx
import { InputForm } from "@oppy/omniv2-form";
import { Card } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/card-title.tsx
import { faTrashCan, Heading2 as Heading22, IconButton as IconButton2 } from "@oppy/omniv2-ui";
import { jsx as jsx41, jsxs as jsxs24 } from "react/jsx-runtime";
function CardTitle({ title, onDelete }) {
  return /* @__PURE__ */ jsxs24("div", { className: "flex items-center justify-between text-secondary", children: [
    /* @__PURE__ */ jsx41("div", { className: "my-2", children: /* @__PURE__ */ jsx41(Heading22, { children: title }) }),
    /* @__PURE__ */ jsx41("div", { children: /* @__PURE__ */ jsx41(
      IconButton2,
      {
        type: "button",
        iconProps: {
          icon: faTrashCan
        },
        rounded: true,
        size: "small",
        variant: "secondary",
        onClick: onDelete
      }
    ) })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/base-action.tsx
import { jsx as jsx42, jsxs as jsxs25 } from "react/jsx-runtime";
function BaseAction({
  children,
  title,
  onDelete,
  controlName
}) {
  return /* @__PURE__ */ jsx42(
    Card,
    {
      className: "shadow-none",
      title: /* @__PURE__ */ jsx42(CardTitle, { title, onDelete }),
      children: /* @__PURE__ */ jsxs25("div", { className: "flex flex-col gap-y-3", children: [
        /* @__PURE__ */ jsx42(
          InputForm,
          {
            name: `${controlName}.label`,
            placeholder: "Libell\xE9",
            max: 25,
            hasEmojiPicker: true
          }
        ),
        children
      ] })
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/create-calendar-event-action.tsx
import {
  CalendarForm,
  InputForm as InputForm2,
  SwitchForm,
  TextAreaForm
} from "@oppy/omniv2-form";
import { jsx as jsx43, jsxs as jsxs26 } from "react/jsx-runtime";
function CreateCalendarEventAction({
  onDelete,
  controlName,
  control,
  watch
}) {
  const hasMetadata = watch(`${controlName}.hasMetadata`);
  return /* @__PURE__ */ jsxs26(
    BaseAction,
    {
      title: "Rendez-vous",
      onDelete,
      controlName,
      children: [
        /* @__PURE__ */ jsx43(
          InputForm2,
          {
            name: `${controlName}.title`,
            placeholder: "Titre",
            hasEmojiPicker: true
          }
        ),
        /* @__PURE__ */ jsx43(
          TextAreaForm,
          {
            name: `${controlName}.description`,
            placeholder: "Description",
            hasEmojiPicker: true
          }
        ),
        /* @__PURE__ */ jsx43(
          SwitchForm,
          {
            control,
            name: `${controlName}.hasMetadata`,
            label: "Utiliser les metadata",
            condition: [true, false]
          }
        ),
        hasMetadata ? /* @__PURE__ */ jsxs26("div", { className: "grid gap-2 sm:grid-cols-2", children: [
          /* @__PURE__ */ jsx43(
            InputForm2,
            {
              name: `${controlName}.startTime`,
              placeholder: "Date de d\xE9but"
            }
          ),
          /* @__PURE__ */ jsx43(
            InputForm2,
            {
              name: `${controlName}.endTime`,
              placeholder: "Date de fin"
            }
          )
        ] }) : /* @__PURE__ */ jsxs26("div", { className: "grid gap-2 sm:grid-cols-2", children: [
          /* @__PURE__ */ jsx43(
            CalendarForm,
            {
              showTime: true,
              name: `${controlName}.startTime`,
              placeholder: "Date de d\xE9but"
            }
          ),
          /* @__PURE__ */ jsx43(
            CalendarForm,
            {
              showTime: true,
              name: `${controlName}.endTime`,
              placeholder: "Date de fin"
            }
          )
        ] })
      ]
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/phone-action.tsx
import { InputForm as InputForm3 } from "@oppy/omniv2-form";
import { jsx as jsx44 } from "react/jsx-runtime";
function PhoneAction({
  onDelete,
  controlName
}) {
  return /* @__PURE__ */ jsx44(
    BaseAction,
    {
      title: "Num\xE9ro de t\xE9l\xE9phone",
      onDelete,
      controlName,
      children: /* @__PURE__ */ jsx44(
        InputForm3,
        {
          name: `${controlName}.number`,
          placeholder: "Num\xE9ro de t\xE9l\xE9phone"
        }
      )
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/reply-action.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
function ReplyAction({
  onDelete,
  controlName
}) {
  return /* @__PURE__ */ jsx45(BaseAction, { controlName, title: "R\xE9ponse", onDelete });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/url-action.tsx
import { InputForm as InputForm4 } from "@oppy/omniv2-form";
import { jsx as jsx46 } from "react/jsx-runtime";
function UrlAction({
  onDelete,
  controlName
}) {
  return /* @__PURE__ */ jsx46(
    BaseAction,
    {
      controlName,
      title: "Lien hypertexte",
      onDelete,
      children: /* @__PURE__ */ jsx46(InputForm4, { name: `${controlName}.href`, placeholder: "Lien hypertexte" })
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/view-location-action/view-location-action.tsx
import { AutoCompleteForm, InputForm as InputForm5, SwitchForm as SwitchForm2 } from "@oppy/omniv2-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/view-location-action/hooks/use-view-location-action.tsx
import { useCallback as useCallback6, useState as useState3 } from "react";
function useViewLocationAction() {
  const [suggestions, setSuggestions] = useState3([]);
  const triggerSearchAdresse = useSearchAdresse();
  const autoCompleteMethod = useCallback6(
    async (event) => {
      const adresses = await triggerSearchAdresse(event.query);
      setSuggestions(adresses);
    },
    [triggerSearchAdresse]
  );
  return { suggestions, autoCompleteMethod };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-type/view-location-action/view-location-action.tsx
import { jsx as jsx47, jsxs as jsxs27 } from "react/jsx-runtime";
function ViewLocationAction({
  onDelete,
  controlName,
  control,
  watch
}) {
  const { suggestions, autoCompleteMethod } = useViewLocationAction();
  const hasMetadata = watch(`${controlName}.hasMetadata`);
  return /* @__PURE__ */ jsxs27(
    BaseAction,
    {
      title: "Localisation",
      onDelete,
      controlName,
      children: [
        /* @__PURE__ */ jsx47(
          SwitchForm2,
          {
            control,
            name: `${controlName}.hasMetadata`,
            label: "Utiliser les metadata",
            condition: [true, false]
          }
        ),
        hasMetadata ? /* @__PURE__ */ jsxs27("div", { className: "grid gap-2 sm:grid-cols-2", children: [
          /* @__PURE__ */ jsx47("div", { className: "col-span-2", children: /* @__PURE__ */ jsx47(
            InputForm5,
            {
              name: `${controlName}.location.label`,
              placeholder: "Localisation"
            }
          ) }),
          /* @__PURE__ */ jsx47(
            InputForm5,
            {
              name: `${controlName}.location.latLong.latitude`,
              placeholder: "Latitude"
            }
          ),
          /* @__PURE__ */ jsx47(
            InputForm5,
            {
              name: `${controlName}.location.latLong.longitude`,
              placeholder: "Longitude"
            }
          )
        ] }) : /* @__PURE__ */ jsx47(
          AutoCompleteForm,
          {
            field: "label",
            name: `${controlName}.location`,
            placeholder: "Localisation",
            suggestions,
            completeMethod: autoCompleteMethod
          }
        )
      ]
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/action-form.tsx
import { jsx as jsx48 } from "react/jsx-runtime";
var ActionTypeComponents = {
  url: UrlAction,
  call: PhoneAction,
  reply: ReplyAction,
  createCalendarEventAction: CreateCalendarEventAction,
  viewLocationAction: ViewLocationAction
};
function ActionForm2({
  index,
  onDelete,
  controlName,
  control,
  watch
}) {
  const actionType = useWatch({
    name: `${controlName}.actionType`
  });
  const ActionTypeComponent = ActionTypeComponents[actionType];
  if (!ActionTypeComponent) return null;
  return /* @__PURE__ */ jsx48(
    ActionTypeComponent,
    {
      index,
      onDelete,
      controlName,
      control,
      watch
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/actions.constants.ts
import {
  faCalendarAlt as faCalendarAlt3,
  faLink as faLink2,
  faLocationDot as faLocationDot3,
  faPhone as faPhone3,
  faReply as faReply3
} from "@oppy/omniv2-ui";
var MAX_ACTIONS = 10;
function getOptions(disabledConnections) {
  return [
    {
      iconProps: {
        icon: faLink2
      },
      label: "Lien",
      value: {
        actionType: "url",
        label: "",
        href: "",
        postbackData: ""
      }
    },
    {
      iconProps: {
        icon: faPhone3
      },
      label: "Num\xE9ro de t\xE9l\xE9phone",
      value: {
        actionType: "call",
        label: "",
        number: "",
        postbackData: ""
      }
    },
    {
      iconProps: {
        icon: faReply3
      },
      label: "R\xE9ponse",
      disabled: disabledConnections,
      value: {
        actionType: "reply",
        label: "",
        postbackData: ""
      }
    },
    {
      iconProps: {
        icon: faCalendarAlt3
      },
      label: "Rendez-vous",
      value: {
        actionType: "createCalendarEventAction",
        label: "",
        postbackData: "",
        title: "",
        description: "",
        startTime: null,
        endTime: null,
        hasMetadata: false
      }
    },
    {
      iconProps: {
        icon: faLocationDot3
      },
      label: "Localisation",
      value: {
        actionType: "viewLocationAction",
        label: "",
        postbackData: "",
        location: null,
        hasMetadata: false
      }
    }
  ];
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/actions-explanation-tooltip.tsx
import { Tooltip as Tooltip2 } from "@oppy/omniv2-ui";
import { jsx as jsx49, jsxs as jsxs28 } from "react/jsx-runtime";
function ActionsExplanationTooltip() {
  return /* @__PURE__ */ jsx49(Tooltip2, { target: `#actions-explanation`, children: /* @__PURE__ */ jsxs28("div", { className: "max-w-sm space-y-3", children: [
    /* @__PURE__ */ jsxs28("p", { children: [
      "Permet de proposer directement sur la carte enrichie des",
      " ",
      /* @__PURE__ */ jsx49("b", { children: "actions" }),
      " pouvant effectuer :"
    ] }),
    /* @__PURE__ */ jsxs28("ul", { className: "list-disc pl-4", children: [
      /* @__PURE__ */ jsxs28("li", { children: [
        "Une navigation vers un ",
        /* @__PURE__ */ jsx49("b", { children: "lien url" })
      ] }),
      /* @__PURE__ */ jsxs28("li", { children: [
        "Composer un ",
        /* @__PURE__ */ jsx49("b", { children: "num\xE9ro de t\xE9l\xE9phone" })
      ] }),
      /* @__PURE__ */ jsxs28("li", { children: [
        /* @__PURE__ */ jsx49("b", { children: "R\xE9pondre" }),
        " \xE0 un message de l'utilisateur"
      ] }),
      /* @__PURE__ */ jsxs28("li", { children: [
        /* @__PURE__ */ jsx49("b", { children: "Plannifier un rendez-vous" }),
        " sur son emplois du temps"
      ] }),
      /* @__PURE__ */ jsxs28("li", { children: [
        /* @__PURE__ */ jsx49("b", { children: "Localiser" }),
        " un endroit depuis Google Maps"
      ] })
    ] })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/actions/actions-form.tsx
import { jsx as jsx50, jsxs as jsxs29 } from "react/jsx-runtime";
var ActionOptionsTemplate = function({
  iconProps,
  label
}) {
  return /* @__PURE__ */ jsxs29("div", { className: "flex items-center gap-x-3 py-1 leading-none", children: [
    /* @__PURE__ */ jsx50(Icon12, { ...iconProps }),
    label
  ] });
};
function Actions({
  isCarousel = false,
  controlName,
  control,
  watch
}) {
  const { fields, append, remove, update } = useFieldArray({
    name: controlName
  });
  const disabledConnections = useDisabledConnections();
  const options = useMemo7(
    () => getOptions(isCarousel || disabledConnections),
    [disabledConnections, isCarousel]
  );
  const replace = useCallback7(
    (actionType, index) => {
      update(index, {
        label: "",
        actionType,
        postbackData: generateUniqueIdentifier("actions")
      });
    },
    [update]
  );
  const getOptionValue = useCallback7(
    (actionType) => options.find((option) => option.value.actionType === actionType)?.value,
    [options]
  );
  const addMoreActions = useCallback7(
    () => append({
      label: "",
      actionType: "reply",
      postbackData: generateUniqueIdentifier("actions")
    }),
    [append]
  );
  return /* @__PURE__ */ jsxs29("div", { className: "my-3 flex flex-col gap-y-3", children: [
    /* @__PURE__ */ jsxs29("div", { className: "flex flex-wrap items-center justify-between", children: [
      /* @__PURE__ */ jsxs29("div", { className: "flex flex-initial items-center gap-2", children: [
        /* @__PURE__ */ jsx50(Heading23, { children: "Proposer a l'utilisateur d'int\xE9rargir" }),
        /* @__PURE__ */ jsx50(ActionsExplanationTooltip, {}),
        /* @__PURE__ */ jsx50(
          "div",
          {
            id: `actions-explanation`,
            className: "flex size-5 items-center justify-center rounded-full border border-gray-500 text-gray-500",
            children: /* @__PURE__ */ jsx50(Icon12, { icon: faQuestion })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs29(
        Button2,
        {
          name: "suggestions",
          type: "button",
          disabled: fields.length >= MAX_ACTIONS || disabledConnections,
          onClick: addMoreActions,
          children: [
            /* @__PURE__ */ jsx50(Icon12, { className: "mr-2", icon: faPlus2 }),
            /* @__PURE__ */ jsx50("span", { children: "Ajouter une Action" })
          ]
        }
      )
    ] }),
    fields.map((field, index) => /* @__PURE__ */ jsxs29(Card2, { children: [
      /* @__PURE__ */ jsx50(
        Select,
        {
          value: getOptionValue(field.actionType),
          itemTemplate: ActionOptionsTemplate,
          name: `${controlName}.${index}`,
          options,
          placeholder: "",
          onChange: (event) => replace(event.value.actionType, index)
        }
      ),
      /* @__PURE__ */ jsx50(
        ActionForm2,
        {
          index,
          controlName: `${controlName}.${index}`,
          onDelete: () => remove(index),
          control,
          watch
        },
        field.id
      )
    ] }, field.id))
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-texts-form.tsx
import { Button as Button3, faPlus as faPlus3, faQuestion as faQuestion2, Heading2 as Heading24, Icon as Icon13 } from "@oppy/omniv2-ui";
import { generateUniqueIdentifier as generateUniqueIdentifier2 } from "@oppy/omniv2-utils";
import { useCallback as useCallback9, useEffect as useEffect2, useMemo as useMemo8, useState as useState5 } from "react";
import { useFieldArray as useFieldArray3 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-text-constants.ts
var MAX_FREE_TEXT_FIELDS_EQUAL_TO = 4;
var MAX_FREE_TEXT_FIELDS = 1;

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-text-form.tsx
import { Fragment as Fragment9, useCallback as useCallback8 } from "react";
import { useFieldArray as useFieldArray2 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-text-form-card.tsx
import { SelectForm } from "@oppy/omniv2-form";
import { Card as Card3 } from "@oppy/omniv2-ui";
import { jsx as jsx51, jsxs as jsxs30 } from "react/jsx-runtime";
function FreeTextFormCard({
  children,
  index,
  options,
  replace
}) {
  return /* @__PURE__ */ jsx51(Card3, { children: /* @__PURE__ */ jsxs30("div", { className: "flex items-start gap-4", children: [
    /* @__PURE__ */ jsx51(
      SelectForm,
      {
        placeholder: "Type",
        options,
        name: `freeTextResponses.${index}.mode`,
        onChange: (event) => replace(index, event.target.value)
      }
    ),
    children
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/modes/free-text-different-from-form.tsx
import { InputForm as InputForm6 } from "@oppy/omniv2-form";
import { faTrashCan as faTrashCan2, IconButton as IconButton3 } from "@oppy/omniv2-ui";
import { jsx as jsx52, jsxs as jsxs31 } from "react/jsx-runtime";
function FreeTextDifferentFromForm({
  control,
  onDelete,
  placeholder,
  index
}) {
  return /* @__PURE__ */ jsx52("div", { className: "flex w-full flex-col gap-y-2", children: /* @__PURE__ */ jsxs31("div", { className: "flex gap-x-2", children: [
    /* @__PURE__ */ jsx52("div", { className: "flex-1", children: /* @__PURE__ */ jsx52(
      InputForm6,
      {
        name: `freeTextResponses.${index}.matchers.0.value`,
        control,
        placeholder
      }
    ) }),
    /* @__PURE__ */ jsx52("div", { className: "h-[50px] w-12", children: /* @__PURE__ */ jsx52(
      IconButton3,
      {
        onClick: onDelete,
        variant: "secondary",
        size: "small",
        fullSize: true,
        iconProps: {
          icon: faTrashCan2
        }
      }
    ) })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/modes/free-text-equals-to-form.tsx
import { InputForm as InputForm7 } from "@oppy/omniv2-form";
import { faTrashCan as faTrashCan3, IconButton as IconButton4 } from "@oppy/omniv2-ui";
import { jsx as jsx53, jsxs as jsxs32 } from "react/jsx-runtime";
function FreeTextEqualsToForm({
  control,
  onDelete,
  placeholder,
  index,
  matcherIdx
}) {
  return /* @__PURE__ */ jsx53("div", { className: "flex w-full flex-col gap-y-2", children: /* @__PURE__ */ jsxs32("div", { className: "flex gap-x-2", children: [
    /* @__PURE__ */ jsx53("div", { className: "flex-1", children: /* @__PURE__ */ jsx53(
      InputForm7,
      {
        name: `freeTextResponses.${index}.matchers.${matcherIdx}.value`,
        control,
        placeholder
      }
    ) }),
    /* @__PURE__ */ jsx53("div", { className: "h-[50px] w-12", children: /* @__PURE__ */ jsx53(
      IconButton4,
      {
        onClick: onDelete,
        variant: "secondary",
        size: "small",
        fullSize: true,
        type: "button",
        iconProps: {
          icon: faTrashCan3
        }
      }
    ) })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/modes/free-text-free-response-form.tsx
import { faTrashCan as faTrashCan4, IconButton as IconButton5 } from "@oppy/omniv2-ui";
import { jsx as jsx54, jsxs as jsxs33 } from "react/jsx-runtime";
function FreeTextFreeResponseForm({ onDelete }) {
  return /* @__PURE__ */ jsxs33("div", { className: "flex w-full items-center gap-x-2", children: [
    /* @__PURE__ */ jsx54("label", { className: "flex-1", children: "Pouvoir r\xE9agir a n'importe quel r\xE9ponse de l'utilisateur directement sur le sc\xE9nario" }),
    /* @__PURE__ */ jsx54("div", { className: "h-[50px] w-12", children: /* @__PURE__ */ jsx54(
      IconButton5,
      {
        type: "button",
        onClick: onDelete,
        variant: "secondary",
        size: "small",
        fullSize: true,
        iconProps: {
          icon: faTrashCan4
        }
      }
    ) })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/modes/free-text-regex-form.tsx
import { InputForm as InputForm8, SelectForm as SelectForm2 } from "@oppy/omniv2-form";
import { faTrashCan as faTrashCan5, IconButton as IconButton6, Input } from "@oppy/omniv2-ui";
import { useState as useState4 } from "react";
import { useController, useFormContext, useWatch as useWatch2 } from "react-hook-form";
import { jsx as jsx55, jsxs as jsxs34 } from "react/jsx-runtime";
var REGEX_TYPE_OPTIONS = [
  ...PREDEFINED_REGEX.map((regex) => ({
    value: regex.id,
    label: regex.label
  })),
  { value: "custom", label: "Personnalis\xE9e" }
];
function FreeTextRegexForm({
  control,
  onDelete,
  index,
  matcherIdx
}) {
  const [testInputValue, setTestInputValue] = useState4("");
  const [testInputError, setTestInputError] = useState4("");
  const regexValue = useWatch2({
    control,
    name: `freeTextResponses.${index}.matchers.${matcherIdx}.value`
  });
  const regexType = useWatch2({
    control,
    name: `freeTextResponses.${index}.matchers.${matcherIdx}.type`
  });
  const { field: valueField } = useController({
    control,
    name: `freeTextResponses.${index}.matchers.${matcherIdx}.value`
  });
  const { clearErrors } = useFormContext();
  const handleRegexTypeChange = (event) => {
    const newType = event.value;
    const fieldName = `freeTextResponses.${index}.matchers.${matcherIdx}.value`;
    clearErrors(fieldName);
    setTestInputValue("");
    setTestInputError("");
    if (isPredefinedRegexType(newType)) {
      const predefinedRegex = getPredefinedRegexById(newType);
      if (predefinedRegex) {
        valueField.onChange(predefinedRegex.pattern);
      }
    } else if (newType === "custom") {
      valueField.onChange("");
    }
  };
  const isCustomRegex = regexType === "custom" || !regexType;
  const selectedPredefinedRegex = regexType && isPredefinedRegexType(regexType) ? getPredefinedRegexById(regexType) : null;
  const isValidRegex = (() => {
    if (!regexValue || regexValue.trim() === "") return null;
    try {
      new RegExp(regexValue);
      return true;
    } catch {
      return false;
    }
  })();
  const handleTestInputChange = (event) => {
    const value = event.target.value;
    setTestInputValue(value);
    setTestInputError("");
    if (!regexValue || regexValue.trim() === "" || isValidRegex !== true) {
      return;
    }
    if (!value || value.trim() === "") {
      return;
    }
    const regex = new RegExp(regexValue);
    const matches = regex.test(value);
    if (!matches) {
      setTestInputError("Aucune correspondance trouv\xE9e");
    }
  };
  return /* @__PURE__ */ jsxs34("div", { className: "flex w-full items-start space-x-3", children: [
    /* @__PURE__ */ jsx55("div", { className: "shrink-0", children: /* @__PURE__ */ jsx55(
      SelectForm2,
      {
        name: `freeTextResponses.${index}.matchers.${matcherIdx}.type`,
        control,
        options: REGEX_TYPE_OPTIONS,
        placeholder: "Type de regex",
        onChange: handleRegexTypeChange
      }
    ) }),
    /* @__PURE__ */ jsx55("div", { className: "min-w-0 flex-1", children: /* @__PURE__ */ jsx55(
      InputForm8,
      {
        name: `freeTextResponses.${index}.matchers.${matcherIdx}.value`,
        control,
        placeholder: isCustomRegex ? "Saisir votre regex..." : selectedPredefinedRegex?.label || "Regex pr\xE9d\xE9finie",
        disabled: !isCustomRegex
      }
    ) }),
    /* @__PURE__ */ jsx55("div", { className: "w-40 min-w-0 shrink", children: /* @__PURE__ */ jsx55(
      Input,
      {
        value: testInputValue,
        onChange: handleTestInputChange,
        placeholder: "Texte \xE0 tester...",
        disabled: !(regexValue && regexValue.trim() && isValidRegex === true),
        error: testInputError
      }
    ) }),
    /* @__PURE__ */ jsx55("div", { className: "h-[50px] w-12 shrink-0", children: /* @__PURE__ */ jsx55(
      IconButton6,
      {
        onClick: onDelete,
        variant: "secondary",
        size: "small",
        fullSize: true,
        type: "button",
        iconProps: {
          icon: faTrashCan5
        }
      }
    ) })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-text-form.tsx
import { Fragment as Fragment10, jsx as jsx56 } from "react/jsx-runtime";
function FreeTextForm({
  index,
  onDelete,
  control,
  options,
  watch,
  replace
}) {
  const { fields: matcherFields, remove } = useFieldArray2({
    name: `freeTextResponses.${index}.matchers`
  });
  const handleOnDelete = useCallback8(
    (matcherIdx) => () => {
      if (matcherFields.length > 1) {
        remove(matcherIdx);
      } else {
        onDelete?.();
      }
    },
    [matcherFields.length, onDelete, remove]
  );
  const placeholderSelector = useCallback8(
    (selectKey) => options.find(({ value }) => value === selectKey)?.label || "",
    [options]
  );
  const formMode = watch(`freeTextResponses.${index}.mode`);
  switch (formMode) {
    case "DIFFERENT_FROM" /* DIFFERENT_FROM */:
      return /* @__PURE__ */ jsx56(FreeTextFormCard, { index, options, replace, children: /* @__PURE__ */ jsx56(
        FreeTextDifferentFromForm,
        {
          control,
          index,
          placeholder: placeholderSelector("DIFFERENT_FROM" /* DIFFERENT_FROM */),
          onDelete
        }
      ) });
    case "FREE_RESPONSE" /* FREE_RESPONSE */:
      return /* @__PURE__ */ jsx56(FreeTextFormCard, { index, options, replace, children: /* @__PURE__ */ jsx56(FreeTextFreeResponseForm, { onDelete }) });
    case "REGEX" /* REGEX */:
      return /* @__PURE__ */ jsx56(Fragment9, { children: matcherFields.map(({ id }, fieldIndex) => /* @__PURE__ */ jsx56(
        FreeTextFormCard,
        {
          index,
          options,
          replace,
          children: /* @__PURE__ */ jsx56(
            FreeTextRegexForm,
            {
              control,
              onDelete: handleOnDelete(fieldIndex),
              index,
              matcherIdx: fieldIndex
            }
          )
        },
        id
      )) });
    case "EQUALS_TO" /* EQUALS_TO */:
    default:
      return /* @__PURE__ */ jsx56(Fragment10, { children: matcherFields.map(({ id }, fieldIndex) => /* @__PURE__ */ jsx56(
        FreeTextFormCard,
        {
          index,
          options,
          replace,
          children: /* @__PURE__ */ jsx56(
            FreeTextEqualsToForm,
            {
              control,
              onDelete: handleOnDelete(fieldIndex),
              index,
              matcherIdx: fieldIndex,
              placeholder: placeholderSelector("EQUALS_TO" /* EQUALS_TO */)
            }
          )
        },
        id
      )) });
  }
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-texts-explanation-tooltip.tsx
import { Tooltip as Tooltip3 } from "@oppy/omniv2-ui";
import { jsx as jsx57, jsxs as jsxs35 } from "react/jsx-runtime";
function FreeTextExplanationTooltip() {
  return /* @__PURE__ */ jsx57(Tooltip3, { target: `#free-answer-explanation`, children: /* @__PURE__ */ jsxs35("div", { className: "max-w-md space-y-3", children: [
    /* @__PURE__ */ jsxs35("p", { children: [
      "Il est possible de r\xE9agir aux r\xE9ponses d'un utilisateur, autres que les suggestions ou actions pr\xE9vues dans le sc\xE9nario, en utilisant l'un des ",
      /* @__PURE__ */ jsx57("b", { children: "quatre modes suivants" }),
      " :"
    ] }),
    /* @__PURE__ */ jsxs35("ul", { className: " list-disc pl-4", children: [
      /* @__PURE__ */ jsxs35("li", { children: [
        /* @__PURE__ */ jsx57("b", { children: "\xC9gal \xE0" }),
        " : permet de r\xE9agir \xE0 une ou plusieurs r\xE9ponses exactes de l'utilisateur."
      ] }),
      /* @__PURE__ */ jsxs35("li", { children: [
        /* @__PURE__ */ jsx57("b", { children: "Diff\xE9rent de" }),
        " : permet de r\xE9agir \xE0 une ou plusieurs r\xE9ponses qui ne correspondent pas aux r\xE9ponses attendues."
      ] }),
      /* @__PURE__ */ jsxs35("li", { children: [
        /* @__PURE__ */ jsx57("b", { children: "R\xE9ponse libre" }),
        " : permet de r\xE9agir \xE0 n'importe quelle r\xE9ponse de l'utilisateur, sans condition particuli\xE8re."
      ] }),
      /* @__PURE__ */ jsxs35("li", { children: [
        /* @__PURE__ */ jsx57("b", { children: "Regex" }),
        " : permet de r\xE9agir aux r\xE9ponses qui correspondent \xE0 un motif d'expression r\xE9guli\xE8re, offrant une validation flexible et avanc\xE9e des formats de texte (email, num\xE9ro de t\xE9l\xE9phone, etc.)."
      ] })
    ] }),
    /* @__PURE__ */ jsxs35("p", { children: [
      /* @__PURE__ */ jsx57("b", { children: "Attention" }),
      " : ces modes de r\xE9ponse ne sont ",
      /* @__PURE__ */ jsx57("b", { children: "pas cumulables" }),
      ". Un seul mode peut \xEAtre utilis\xE9 \xE0 la fois."
    ] })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/free-texts/free-texts-form.tsx
import { jsx as jsx58, jsxs as jsxs36 } from "react/jsx-runtime";
var OPTIONS = Object.values(FreeTextFieldMode).map((val) => ({
  label: {
    EQUALS_TO: "Est \xE9gale",
    DIFFERENT_FROM: "Diff\xE8rent de",
    FREE_RESPONSE: "R\xE9ponse libre",
    REGEX: "Regex"
  }[val],
  value: val
}));
function FreeTextsForm({ control, watch }) {
  const { fields, remove, update, append } = useFieldArray3({
    control,
    name: "freeTextResponses"
  });
  const [pendingAppend, setPendingAppend] = useState5(null);
  useEffect2(() => {
    if (pendingAppend) {
      append(pendingAppend);
      setPendingAppend(null);
    }
  }, [pendingAppend, append]);
  const maxFreeTextField = useMemo8(
    () => fields.length && fields[0].mode !== "EQUALS_TO" /* EQUALS_TO */ ? MAX_FREE_TEXT_FIELDS : MAX_FREE_TEXT_FIELDS_EQUAL_TO,
    [fields]
  );
  const disabledConnections = useDisabledConnections();
  const addMoreAnswerDisabled = useMemo8(
    () => disabledConnections || fields?.length && fields[0].matchers.length >= maxFreeTextField || fields.some((field) => field.mode !== "EQUALS_TO" /* EQUALS_TO */),
    [disabledConnections, fields, maxFreeTextField]
  );
  const replace = useCallback9(
    (index, mode) => {
      const baseMatcher = {
        value: "",
        postbackData: generateUniqueIdentifier2("freeTextResponses")
      };
      const matchers = mode === "REGEX" /* REGEX */ ? [
        {
          ...baseMatcher,
          type: "custom"
        }
      ] : [
        baseMatcher
      ];
      remove(index);
      setPendingAppend({ mode, matchers });
    },
    [remove]
  );
  const addMoreOption = useCallback9(() => {
    const newMatcher = {
      value: "",
      postbackData: generateUniqueIdentifier2("freeTextResponses")
    };
    if (fields.length) {
      const existingField = fields[0];
      const updatedMatcher = existingField.mode === "REGEX" /* REGEX */ ? { ...newMatcher, type: "custom" } : newMatcher;
      update(0, {
        ...existingField,
        matchers: [
          ...existingField.matchers || [],
          updatedMatcher
        ]
      });
    } else {
      append({
        mode: "EQUALS_TO" /* EQUALS_TO */,
        matchers: [newMatcher]
      });
    }
  }, [append, fields, update]);
  return /* @__PURE__ */ jsxs36("div", { className: "my-3 flex flex-col gap-y-3", children: [
    /* @__PURE__ */ jsxs36("div", { className: "flex flex-wrap items-center justify-between", children: [
      /* @__PURE__ */ jsxs36("div", { className: "flex flex-initial items-center gap-2", children: [
        /* @__PURE__ */ jsx58(Heading24, { children: "R\xE9agir aux r\xE9ponses utilisateur" }),
        /* @__PURE__ */ jsx58(FreeTextExplanationTooltip, {}),
        /* @__PURE__ */ jsx58(
          "div",
          {
            id: `free-answer-explanation`,
            className: "flex size-5 items-center justify-center rounded-full border border-gray-500 text-gray-500",
            children: /* @__PURE__ */ jsx58(Icon13, { icon: faQuestion2 })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs36(
        Button3,
        {
          name: "freeTextResponses",
          type: "button",
          className: "w-fit",
          disabled: addMoreAnswerDisabled,
          onClick: addMoreOption,
          children: [
            /* @__PURE__ */ jsx58(Icon13, { className: "mr-2", icon: faPlus3 }),
            /* @__PURE__ */ jsx58("span", { children: "Ajouter une r\xE8gle" })
          ]
        }
      )
    ] }),
    fields?.map((field, index) => /* @__PURE__ */ jsx58(
      FreeTextForm,
      {
        index,
        onDelete: () => remove(index),
        control,
        watch,
        replace,
        options: OPTIONS
      },
      field.id
    ))
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/pictures/picture-form.tsx
import {
  InputForm as InputForm9,
  PictureUploaderForm,
  SelectForm as SelectForm3,
  SwitchForm as SwitchForm3
} from "@oppy/omniv2-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/validations/actions.schema.ts
import { dateValidation, phoneNumberValidation } from "@oppy/omniv2-form";
import { z } from "zod";
var PHONE_ACTION_SCHEMA = z.object({
  actionType: z.literal("call"),
  postbackData: z.string(),
  label: z.string().refine(
    (val) => /^([^{}]|{{[^{}]+}})*$/.test(val) && val.length >= 1 && val.length <= 25,
    {
      message: "Le libell\xE9 doit \xEAtre compris entre 1 et 25 caract\xE8res ou une metadata au format {{...}}"
    }
  ),
  number: z.string().superRefine((val, ctx) => {
    if (val.startsWith("{{")) {
      if (!val.endsWith("}}")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Une metadata doit \xEAtre au format {{...}}"
        });
      }
      return;
    }
    try {
      phoneNumberValidation("Le format du num\xE9ro de t\xE9l\xE9phone est invalide")(
        val,
        ctx
      );
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le format du num\xE9ro de t\xE9l\xE9phone est invalide"
      });
    }
  })
});
var URL_ACTION_SCHEMA = z.object({
  actionType: z.literal("url"),
  postbackData: z.string(),
  label: z.string().refine(
    (val) => {
      if (val.startsWith("{{")) {
        return val.endsWith("}}");
      }
      return val.length >= 1 && val.length <= 25;
    },
    {
      message: "Le libell\xE9 doit \xEAtre compris entre 1 et 25 caract\xE8res ou une metadata au format {{...}}"
    }
  ),
  href: z.string().superRefine((val, ctx) => {
    if (val.startsWith("{{")) {
      if (!val.endsWith("}}")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Une metadata doit \xEAtre format {{...}}"
        });
      }
      return;
    }
    if (!z.string().url().safeParse(val).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le lien hypertexte doit \xEAtre valide"
      });
    }
  })
});
var REPLY_ACTION_SCHEMA = z.object({
  actionType: z.literal("reply"),
  postbackData: z.string(),
  label: z.string().refine(
    (val) => {
      if (val.startsWith("{{")) {
        return val.endsWith("}}");
      }
      return val.length >= 1 && val.length <= 25;
    },
    {
      message: "Le libell\xE9 doit \xEAtre compris entre 1 et 25 caract\xE8res ou une metadata au format {{...}}"
    }
  )
});
var CALENDAR_ACTION_SCHEMA = z.object({
  actionType: z.literal("createCalendarEventAction"),
  postbackData: z.string(),
  label: z.string().refine(
    (val) => /^([^{}]|{{[^{}]+}})*$/.test(val) && val.length >= 1 && val.length <= 25,
    {
      message: "Le libell\xE9 doit \xEAtre compris entre 1 et 25 caract\xE8res ou une metadata au format {{...}}"
    }
  ),
  title: z.string().refine(
    (val) => /^([^{}]|{{[^{}]+}})*$/.test(val) && val.length >= 1 && val.length <= 100,
    {
      message: "Le titre doit \xEAtre compris entre 1 et 100 caract\xE8res ou une metadata au format {{...}}"
    }
  ),
  description: z.string().refine(
    (val) => /^([^{}]|{{[^{}]+}})*$/.test(val) && val.length >= 1 && val.length <= 500,
    {
      message: "La description doit \xEAtre compris entre 1 et 500 caract\xE8res ou une metadata au format {{...}}"
    }
  ),
  hasMetadata: z.boolean().default(false),
  startTime: z.union([
    z.string().refine(
      (val) => val.startsWith("{{") && val.endsWith("}}"),
      "La date de d\xE9but peut \xEAtre une metadata au format {{...}}"
    ),
    dateValidation("La date du d\xE9but du rendez-vous est obligatoire")
  ]),
  endTime: z.union([
    z.string().refine(
      (val) => val.startsWith("{{") && val.endsWith("}}"),
      "La date de fin peut \xEAtre une metadata au format {{...}}"
    ),
    dateValidation("La date de fin du rendez-vous est obligatoire")
  ])
}).superRefine(({ startTime, endTime }, ctx) => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  if (startDate > endDate)
    return ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "La date de fin doit d\xE9passer la date de d\xE9but du rendez-vous",
      path: ["endTime"]
    });
});
var VIEW_LOCATION_ACTION_SCHEMA = z.object({
  actionType: z.literal("viewLocationAction"),
  postbackData: z.string(),
  label: z.string().refine(
    (val) => /^([^{}]|{{[^{}]+}})*$/.test(val) && val.length >= 1 && val.length <= 25,
    {
      message: "Le libell\xE9 doit \xEAtre compris entre 1 et 25 caract\xE8res ou une metadata au format {{...}}"
    }
  ),
  location: z.object(
    {
      id: z.string().optional(),
      label: z.string().refine(
        (val) => /^([^{}]|{{[^{}]+}})*$/.test(val) && val.length >= 1 && val.length <= 100,
        {
          message: "Le libell\xE9 doit \xEAtre compris entre 1 et 100 caract\xE8res ou une metadata au format {{...}}"
        }
      ),
      latLong: z.object({
        latitude: z.union([
          z.string().refine(
            (val) => val.startsWith("{{") && val.endsWith("}}"),
            "La latitude doit \xEAtre en degr\xE9 entre -90 et 90, ou une metadata au format {{...}}"
          ),
          z.coerce.number().min(-90).max(90).transform((latitude) => String(latitude))
        ]),
        longitude: z.union([
          z.string().refine(
            (val) => val.startsWith("{{") && val.endsWith("}}"),
            "La longitude doit \xEAtre en degr\xE9 entre -180 et 180, ou une metadata au format {{...}}"
          ),
          z.coerce.number().min(-180).max(180).transform((longitude) => String(longitude))
        ])
      })
    },
    { invalid_type_error: "Le choix de la localisation est obligatoire" }
  ),
  hasMetadata: z.boolean().default(false)
});
var ACTION_SCHEMA = z.union([
  CALENDAR_ACTION_SCHEMA,
  PHONE_ACTION_SCHEMA,
  URL_ACTION_SCHEMA,
  REPLY_ACTION_SCHEMA,
  VIEW_LOCATION_ACTION_SCHEMA
]);
var ACTION_SCHEMAS = z.array(ACTION_SCHEMA).min(0).max(10, "10 actions au maximum peuvent \xEAtre ajout\xE9es").default([]);

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/validations/free-text.schema.ts
import { z as z2 } from "zod";
var caseInsensitiveAndTrim = (v) => v.value.toLocaleLowerCase().trim();
var EqualsToSchema = z2.object({
  matchers: z2.array(
    z2.object({
      value: z2.string().min(1, {
        message: "La valeur de correspondance ne peut pas \xEAtre vide"
      }),
      postbackData: z2.string()
    })
  ).min(1, {
    message: "Au moins une valeur de correspondance est requise"
  }).max(MAX_FREE_TEXT_FIELDS_EQUAL_TO, {
    message: `Maximum ${MAX_FREE_TEXT_FIELDS_EQUAL_TO} valeurs de correspondance sont autoris\xE9es`
  }).refine(
    (values) => new Set(values.map(caseInsensitiveAndTrim)).size === values.length,
    {
      message: "Les valeurs doivent \xEAtre uniques"
    }
  ).describe("Liste des textes \xE0 faire correspondre"),
  mode: z2.literal("EQUALS_TO" /* EQUALS_TO */)
});
var DifferentFromSchema = z2.object({
  matchers: z2.array(
    z2.object({
      value: z2.string().min(1, {
        message: "La valeur de non-correspondance ne peut pas \xEAtre vide"
      }),
      postbackData: z2.string()
    })
  ).describe("Le texte qui ne doit pas correspondre"),
  mode: z2.literal("DIFFERENT_FROM" /* DIFFERENT_FROM */)
});
var FreeResponseSchema = z2.object({
  matchers: z2.array(
    z2.object({
      postbackData: z2.string()
    })
  ),
  mode: z2.literal("FREE_RESPONSE" /* FREE_RESPONSE */)
});
var RegexSchema = z2.object({
  matchers: z2.array(
    z2.object({
      value: z2.string().min(1, {
        message: "La regex ne peut pas \xEAtre vide"
      }).refine(
        (value) => {
          try {
            new RegExp(value);
            return true;
          } catch {
            return false;
          }
        },
        {
          message: "La regex fournie n'est pas valide"
        }
      ),
      type: z2.string().min(1, {
        message: "Le type de regex est requis"
      }),
      postbackData: z2.string()
    })
  ).min(1, {
    message: "Au moins une regex est requise"
  }).max(1, {
    message: "Une seule regex est autoris\xE9e"
  }),
  mode: z2.literal("REGEX" /* REGEX */)
});
var FREETEXT_SCHEMA = z2.discriminatedUnion("mode", [
  EqualsToSchema,
  DifferentFromSchema,
  FreeResponseSchema,
  RegexSchema
]).array().min(0).max(1, {
  message: "Un seul champ de texte libre est autoris\xE9"
}).optional();

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/validations/picture.schema.ts
import { fileSizeInOctet } from "@oppy/omniv2-utils";
import { z as z3 } from "zod";
var RCS_ALLOWED_IMAGE_ACCEPT = ".jpg,.jpeg,.png";
var RCS_ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png"
];
var resolutionSchema = z3.object({
  width: z3.number(),
  height: z3.number()
});
var metadataPictureSchema = z3.object({
  url: z3.string().nullable()
}).nullable();
var pictureSchema = z3.object({
  name: z3.string().min(1, "Le nom de l'image est obligatoire"),
  src: z3.string().url("L'url de l'image doit \xEAtre valide"),
  size: z3.number().max(fileSizeInOctet(2), "L'image ne doit pas d\xE9passer 2Mo"),
  imageType: z3.string().refine(
    (type) => /^image\/(jpeg|jpg|png)$/i.test(type),
    "Seuls les formats .jpg, .jpeg et .png sont autoris\xE9s pour les images"
  ),
  resolution: resolutionSchema
}).nullable();
var PICTURE_SCHEMA = z3.discriminatedUnion("pictureType", [
  z3.object({
    pictureType: z3.literal(PICTURE_TYPE_METADATA),
    picture: metadataPictureSchema
  }),
  z3.object({
    pictureType: z3.literal(PICTURE_TYPE_UPLOAD),
    picture: pictureSchema
  })
]);

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/validations/suggestions.schema.ts
import { z as z4 } from "zod";
var SUGGESTIONS_SCHEMA = z4.array(ACTION_SCHEMA).min(0).max(11, "11 suggestions au maximum peuvent \xEAtre ajout\xE9es").default([]);

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/pictures/picture-form.tsx
import { jsx as jsx59, jsxs as jsxs37 } from "react/jsx-runtime";
var cardHeightOptions = Object.values(CardHeightEnum).map((val) => ({
  label: {
    MEDIUM: "Moyenne",
    SHORT: "Petite",
    TALL: "Grande"
  }[val],
  value: val
}));
var cardOrientationOptions = Object.values(CardOrientationEnum).map((val) => ({
  label: {
    VERTICAL: "Vertical",
    HORIZONTAL: "Horizontal"
  }[val],
  value: val
}));
var thumbnailImageAlignmentOptions = Object.values(
  CardImageAlignmentEnum
).map((val) => ({
  label: {
    LEFT: "Gauche",
    RIGHT: "Droite"
  }[val],
  value: val
}));
function PictureForm({
  control,
  setValue,
  watch,
  controlName,
  isCarousel
}) {
  const pictureType = watch(
    `${controlName}pictureSource.pictureType`
  );
  const cardOrientation = watch(`${controlName}cardOrientation`);
  const handleChange = (value) => {
    setValue(
      `${controlName}pictureSource.picture`,
      value === PICTURE_TYPE_METADATA ? { url: "" } : null
    );
  };
  const handleChangeOrientation = (value) => {
    setValue(
      `${controlName}cardOrientation`,
      value
    );
    setValue(
      `${controlName}thumbnailImageAlignment`,
      value === "HORIZONTAL" /* HORIZONTAL */ ? "LEFT" /* LEFT */ : void 0
    );
  };
  return /* @__PURE__ */ jsxs37("div", { className: "flex gap-x-4", children: [
    /* @__PURE__ */ jsx59("div", { className: " flex-1", children: pictureType === PICTURE_TYPE_METADATA ? /* @__PURE__ */ jsx59(
      InputForm9,
      {
        control,
        name: `${controlName}pictureSource.picture.url`,
        placeholder: "Image"
      }
    ) : /* @__PURE__ */ jsx59(
      PictureUploaderForm,
      {
        accept: RCS_ALLOWED_IMAGE_ACCEPT,
        allowedImageTypes: [...RCS_ALLOWED_IMAGE_MIME_TYPES],
        control,
        name: `${controlName}pictureSource.picture`,
        placeholder: "Image",
        helperText: cardOrientation === "HORIZONTAL" /* HORIZONTAL */ ? "Ratio accept\xE9s 3:4 (par d\xE9faut)" : "Ratio accept\xE9s 16:9 (par d\xE9faut) / 2:1 / 7:3",
        allowedRatio: cardOrientation === "HORIZONTAL" /* HORIZONTAL */ ? [{ label: "3:4", value: 3 / 4 }] : [
          { label: "16:9", value: 16 / 9 },
          { label: "2:1", value: 2 / 1 },
          { label: "7:3", value: 7 / 3 }
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs37("div", { className: "flex w-[200px] max-w-[200px] shrink-0 flex-col gap-y-4", children: [
      /* @__PURE__ */ jsx59(
        SwitchForm3,
        {
          control,
          name: `${controlName}pictureSource.pictureType`,
          label: "Via URL",
          condition: [PICTURE_TYPE_METADATA, PICTURE_TYPE_UPLOAD],
          onChange: handleChange
        }
      ),
      !isCarousel && /* @__PURE__ */ jsx59(
        SelectForm3,
        {
          placeholder: "Orientation",
          options: cardOrientationOptions,
          name: `${controlName}cardOrientation`,
          onChange: (e) => handleChangeOrientation(e.value)
        }
      ),
      cardOrientation === "HORIZONTAL" /* HORIZONTAL */ ? /* @__PURE__ */ jsx59(
        SelectForm3,
        {
          placeholder: "Alignement",
          options: thumbnailImageAlignmentOptions,
          name: `${controlName}thumbnailImageAlignment`
        }
      ) : /* @__PURE__ */ jsx59(
        SelectForm3,
        {
          placeholder: "Taille d'image",
          options: cardHeightOptions,
          name: `${controlName}cardHeight`
        }
      )
    ] })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/suggestions/suggestions-form.tsx
import { Button as Button4, faPlus as faPlus4, faQuestion as faQuestion3, Heading2 as Heading25, Icon as Icon15 } from "@oppy/omniv2-ui";
import { generateUniqueIdentifier as generateUniqueIdentifier3 } from "@oppy/omniv2-utils";
import { useCallback as useCallback10 } from "react";
import { useFieldArray as useFieldArray4 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/suggestions/suggestion-form.tsx
import { Card as Card4, Icon as Icon14, Select as Select2 } from "@oppy/omniv2-ui";
import { useMemo as useMemo9 } from "react";
import { jsx as jsx60, jsxs as jsxs38 } from "react/jsx-runtime";
var ActionOptionsTemplate2 = function({
  iconProps,
  label
}) {
  return /* @__PURE__ */ jsxs38("div", { className: "flex items-center gap-x-3 py-1 leading-none", children: [
    /* @__PURE__ */ jsx60(Icon14, { ...iconProps }),
    label
  ] });
};
function SuggestionForm({
  controlName,
  control,
  index,
  options,
  field,
  remove,
  replace,
  watch
}) {
  const value = useMemo9(
    () => options.find((option) => option.value.actionType === field.actionType),
    [field.actionType, options]
  );
  return /* @__PURE__ */ jsxs38(Card4, { children: [
    /* @__PURE__ */ jsx60(
      Select2,
      {
        value: value?.value,
        itemTemplate: ActionOptionsTemplate2,
        name: `${controlName}.${index}`,
        options,
        placeholder: "",
        onChange: (event) => replace(event.value.actionType, index)
      }
    ),
    /* @__PURE__ */ jsx60(
      ActionForm2,
      {
        index,
        controlName: `${controlName}.${index}`,
        onDelete: remove,
        control,
        watch
      },
      field.id
    )
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/suggestions/suggestions-explanation-tooltip.tsx
import { Tooltip as Tooltip4 } from "@oppy/omniv2-ui";
import { jsx as jsx61, jsxs as jsxs39 } from "react/jsx-runtime";
function SuggestionsExplanationTooltip() {
  return /* @__PURE__ */ jsx61(Tooltip4, { target: `#suggestions-explanation`, children: /* @__PURE__ */ jsxs39("div", { className: "max-w-sm space-y-3", children: [
    /* @__PURE__ */ jsx61("p", { children: "Permet de proposer directement des bulles de r\xE9ponses pr\xE9d\xE9finit \xE0 l'utilisateur ou permettant d'effectuer :" }),
    /* @__PURE__ */ jsxs39("ul", { className: "list-disc pl-4", children: [
      /* @__PURE__ */ jsxs39("li", { children: [
        "Une navigation vers un ",
        /* @__PURE__ */ jsx61("b", { children: "lien url" })
      ] }),
      /* @__PURE__ */ jsxs39("li", { children: [
        "Composer un ",
        /* @__PURE__ */ jsx61("b", { children: "num\xE9ro de t\xE9l\xE9phone" })
      ] }),
      /* @__PURE__ */ jsxs39("li", { children: [
        /* @__PURE__ */ jsx61("b", { children: "R\xE9pondre" }),
        " \xE0 un message de l'utilisateur"
      ] }),
      /* @__PURE__ */ jsxs39("li", { children: [
        /* @__PURE__ */ jsx61("b", { children: "Plannifier un rendez-vous" }),
        " sur son emplois du temps"
      ] }),
      /* @__PURE__ */ jsxs39("li", { children: [
        /* @__PURE__ */ jsx61("b", { children: "Localiser" }),
        " un endroit depuis Google Maps"
      ] })
    ] })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/suggestions/suggestions-form.tsx
import { jsx as jsx62, jsxs as jsxs40 } from "react/jsx-runtime";
var MAX_SUGGESTIONS = 11;
function SuggestionsForm({
  controlName,
  control,
  watch
}) {
  const { fields, append, remove, update } = useFieldArray4({
    name: "suggestions"
  });
  const disabledConnections = useDisabledConnections();
  const replace = useCallback10(
    (actionType, index) => {
      update(index, {
        label: "",
        actionType,
        postbackData: generateUniqueIdentifier3("suggestion")
      });
    },
    [update]
  );
  const addMoreSuggestions = useCallback10(
    () => append({
      label: "",
      actionType: "reply",
      postbackData: generateUniqueIdentifier3("suggestion")
    }),
    [append]
  );
  return /* @__PURE__ */ jsxs40("div", { className: "my-3 flex flex-col gap-y-3", children: [
    /* @__PURE__ */ jsxs40("div", { className: "flex flex-wrap items-center justify-between", children: [
      /* @__PURE__ */ jsxs40("div", { className: "flex flex-initial items-center gap-2", children: [
        /* @__PURE__ */ jsx62(Heading25, { children: "Sugg\xE9rer des r\xE9ponses ou des actions \xE0 l'utilisateur" }),
        /* @__PURE__ */ jsx62(SuggestionsExplanationTooltip, {}),
        /* @__PURE__ */ jsx62(
          "div",
          {
            id: `suggestions-explanation`,
            className: "flex size-5 items-center justify-center rounded-full border border-gray-500 text-gray-500",
            children: /* @__PURE__ */ jsx62(Icon15, { icon: faQuestion3 })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs40(
        Button4,
        {
          name: "suggestions",
          type: "button",
          disabled: fields.length >= MAX_SUGGESTIONS || disabledConnections,
          onClick: addMoreSuggestions,
          children: [
            /* @__PURE__ */ jsx62(Icon15, { className: "mr-2", icon: faPlus4 }),
            /* @__PURE__ */ jsx62("span", { children: "Ajouter une suggestion" })
          ]
        }
      )
    ] }),
    fields.map((field, index) => /* @__PURE__ */ jsx62(
      SuggestionForm,
      {
        controlName,
        control,
        watch,
        replace,
        remove: () => remove(index),
        index,
        field,
        options: getOptions(disabledConnections)
      },
      index
    ))
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/tabs/card-tab-form.tsx
import { InputForm as InputForm10, TextAreaForm as TextAreaForm2 } from "@oppy/omniv2-form";
import { Heading2 as Heading26, Tab } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/tabs/header-tab-form.tsx
import { cn as cn4, faTriangleExclamation, Icon as Icon16 } from "@oppy/omniv2-ui";
import { memo } from "react";
import { jsx as jsx63, jsxs as jsxs41 } from "react/jsx-runtime";
var HeaderTabForm = memo(
  ({ title, errors, fieldNames }) => {
    const hasErrorInTab = fieldNames.some((fieldName) => !!errors[fieldName]);
    return /* @__PURE__ */ jsxs41(
      "div",
      {
        className: cn4("flex items-center gap-2", {
          "text-error": hasErrorInTab
        }),
        children: [
          title,
          " ",
          hasErrorInTab && /* @__PURE__ */ jsx63(Icon16, { icon: faTriangleExclamation })
        ]
      }
    );
  }
);
var header_tab_form_default = HeaderTabForm;

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/tabs/card-tab-form.tsx
import { Fragment as Fragment11, jsx as jsx64, jsxs as jsxs42 } from "react/jsx-runtime";
var tabs = ({
  control,
  setValue,
  errors,
  watch,
  isCarousel = false,
  controlName,
  indexCarousel
}) => [
  {
    header: /* @__PURE__ */ jsx64(
      header_tab_form_default,
      {
        title: "image",
        errors,
        fieldNames: [
          "pictureSource",
          "cardHeight",
          "cardOrientation",
          "thumbnailImageAlignment"
        ]
      }
    ),
    children: /* @__PURE__ */ jsx64(Fragment11, { children: /* @__PURE__ */ jsx64(
      PictureForm,
      {
        control,
        setValue,
        controlName,
        watch,
        isCarousel,
        indexCarousel
      }
    ) })
  },
  {
    header: /* @__PURE__ */ jsx64(
      header_tab_form_default,
      {
        title: "contenu",
        errors,
        fieldNames: ["title", "description"]
      }
    ),
    children: /* @__PURE__ */ jsxs42("div", { className: "space-y-4", children: [
      /* @__PURE__ */ jsx64(
        InputForm10,
        {
          control,
          name: `${controlName}title`,
          placeholder: "Titre",
          max: 200,
          hasEmojiPicker: true
        }
      ),
      /* @__PURE__ */ jsx64(
        TextAreaForm2,
        {
          control,
          name: `${controlName}description`,
          placeholder: "Description",
          rows: 12,
          maxLength: 2e3,
          hasEmojiPicker: true
        }
      )
    ] })
  },
  {
    header: /* @__PURE__ */ jsx64(
      header_tab_form_default,
      {
        title: "actions",
        errors,
        fieldNames: ["actions"]
      }
    ),
    children: /* @__PURE__ */ jsx64(
      Actions,
      {
        control,
        controlName: `${controlName}actions`,
        isCarousel,
        watch
      }
    )
  },
  {
    header: /* @__PURE__ */ jsx64(
      header_tab_form_default,
      {
        title: "suggestions",
        errors,
        fieldNames: ["suggestions"]
      }
    ),
    children: /* @__PURE__ */ jsx64(
      SuggestionsForm,
      {
        control,
        controlName: `${controlName}suggestions`,
        watch
      }
    )
  },
  {
    header: /* @__PURE__ */ jsx64(
      header_tab_form_default,
      {
        title: "R\xE9ponse libre",
        errors,
        fieldNames: ["freeTextResponses"]
      }
    ),
    children: /* @__PURE__ */ jsx64(
      FreeTextsForm,
      {
        watch,
        control
      }
    )
  }
];
function CardTabForm({
  methods,
  indexCarousel,
  isCarousel
}) {
  const {
    control,
    setValue,
    formState: { errors },
    watch
  } = methods;
  const controlName = isCarousel ? `cards.${indexCarousel}.` : "";
  const richCardError = isCarousel ? errors.cards && errors.cards?.[indexCarousel] ? errors.cards?.[indexCarousel] : {} : errors;
  const tabOption = {
    control,
    setValue,
    errors: richCardError,
    watch,
    isCarousel,
    controlName,
    indexCarousel
  };
  const tabDisplayed = isCarousel ? tabs(tabOption).slice(0, 3) : tabs(tabOption);
  return /* @__PURE__ */ jsxs42(Fragment11, { children: [
    isCarousel && /* @__PURE__ */ jsx64("div", { className: "flex justify-between", children: /* @__PURE__ */ jsxs42(Heading26, { className: "self-center", children: [
      "Carte Enrichie ",
      indexCarousel + 1,
      " "
    ] }) }),
    /* @__PURE__ */ jsx64(Tab, { items: tabDisplayed })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/conversation-reply.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
function ConversationReply({ reply }) {
  const { renderable } = reply;
  if (renderable === false) {
    return null;
  }
  return reply.label && /* @__PURE__ */ jsx65("div", { className: "flex w-full justify-end", children: /* @__PURE__ */ jsx65("p", { className: "max-w-3.5/5 break-all rounded-2xl bg-primary px-3 py-1.5 text-xs text-white", children: reply.label }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/actions.tsx
import { Fragment as Fragment13 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/create-calendar-event-action.tsx
import { faCalendarAlt as faCalendarAlt4 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/default-action.tsx
import { Icon as Icon17 } from "@oppy/omniv2-ui";
import { jsx as jsx66, jsxs as jsxs43 } from "react/jsx-runtime";
function DefaultAction({ icon, label, onClick }) {
  return /* @__PURE__ */ jsxs43(
    "a",
    {
      role: "button",
      className: "text-sm font-bold text-sky-700",
      onClick,
      children: [
        icon && /* @__PURE__ */ jsx66(Icon17, { className: "mr-2", icon }),
        label
      ]
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/create-calendar-event-action.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
function CreateCalendarEventAction2({ data, nodeId }) {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const { actionType, label, postbackData } = data;
  const handleClick = () => {
    const reply = {
      actionType,
      label,
      postbackData,
      type: "action" /* ACTION */,
      renderable: false,
      nodeId
    };
    sendEvent({ type: "reply" /* REPLY */, reply });
  };
  return /* @__PURE__ */ jsx67(DefaultAction, { icon: faCalendarAlt4, label, onClick: handleClick });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/phone-action.tsx
import { faPhone as faPhone4 } from "@oppy/omniv2-ui";
import { jsx as jsx68 } from "react/jsx-runtime";
function PhoneAction2({ data, nodeId }) {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const { actionType, label, postbackData } = data;
  const handleClick = () => {
    const reply = {
      actionType,
      label,
      postbackData,
      type: "action" /* ACTION */,
      renderable: false,
      nodeId
    };
    sendEvent({ type: "reply" /* REPLY */, reply });
  };
  return /* @__PURE__ */ jsx68(DefaultAction, { icon: faPhone4, label, onClick: handleClick });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/reply-action.tsx
import { faReply as faReply4 } from "@oppy/omniv2-ui";
import { jsx as jsx69 } from "react/jsx-runtime";
function ReplyAction2({ data, nodeId }) {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const { actionType, label, postbackData } = data;
  const handleClick = () => {
    const reply = {
      actionType,
      label,
      postbackData,
      type: "action" /* ACTION */,
      nodeId
    };
    sendEvent({ type: "reply" /* REPLY */, reply });
  };
  return /* @__PURE__ */ jsx69(DefaultAction, { label, onClick: handleClick, icon: faReply4 });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/url-action.tsx
import { faGlobeEurope as faGlobeEurope2 } from "@oppy/omniv2-ui";
import { jsx as jsx70 } from "react/jsx-runtime";
function UrlAction2({ data, nodeId }) {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const { actionType, label, postbackData } = data;
  const handleClick = () => {
    const reply = {
      actionType,
      label,
      postbackData,
      type: "action" /* ACTION */,
      renderable: false,
      nodeId
    };
    sendEvent({ type: "reply" /* REPLY */, reply });
  };
  return /* @__PURE__ */ jsx70(DefaultAction, { icon: faGlobeEurope2, label, onClick: handleClick });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action-type/view-location-action.tsx
import { faLocationDot as faLocationDot4 } from "@oppy/omniv2-ui";
import { jsx as jsx71 } from "react/jsx-runtime";
function ViewLocationAction2({ data, nodeId }) {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const { actionType, label, postbackData } = data;
  const handleClick = () => {
    const reply = {
      actionType,
      label,
      postbackData,
      type: "action" /* ACTION */,
      renderable: false,
      nodeId
    };
    sendEvent({ type: "reply" /* REPLY */, reply });
  };
  return /* @__PURE__ */ jsx71(DefaultAction, { icon: faLocationDot4, label, onClick: handleClick });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/action.tsx
import { Fragment as Fragment12, jsx as jsx72, jsxs as jsxs44 } from "react/jsx-runtime";
function Action({ data, nodeId }) {
  const { actionType } = data;
  return /* @__PURE__ */ jsxs44(Fragment12, { children: [
    actionType === "call" && /* @__PURE__ */ jsx72(PhoneAction2, { nodeId, data }),
    actionType === "createCalendarEventAction" && /* @__PURE__ */ jsx72(CreateCalendarEventAction2, { nodeId, data }),
    actionType === "reply" && /* @__PURE__ */ jsx72(ReplyAction2, { nodeId, data }),
    actionType === "url" && /* @__PURE__ */ jsx72(UrlAction2, { nodeId, data }),
    actionType === "viewLocationAction" && /* @__PURE__ */ jsx72(ViewLocationAction2, { nodeId, data })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/actions/actions.tsx
import { jsx as jsx73, jsxs as jsxs45 } from "react/jsx-runtime";
function Actions2({ data, nodeId }) {
  return /* @__PURE__ */ jsx73("div", { className: "flex w-full flex-col items-center gap-y-3 p-2", children: data.map((action, index) => /* @__PURE__ */ jsxs45(Fragment13, { children: [
    index !== 0 && /* @__PURE__ */ jsx73("hr", { className: "h-px w-full border-0 bg-gray-300" }),
    /* @__PURE__ */ jsx73(Action, { nodeId, data: action }, action.postbackData)
  ] }, index)) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/suggestions/suggestions.tsx
import { useHorizontalScrollbar } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/suggestions/suggestion.tsx
import { faCalendarAlt as faCalendarAlt5, Icon as Icon19 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/suggestions/action-types/suggestion-reply-action.tsx
import { Badge as Badge2, faReply as faReply5, Icon as Icon18, Ripple } from "@oppy/omniv2-ui";
import { jsx as jsx74, jsxs as jsxs46 } from "react/jsx-runtime";
function SuggestionReplyAction({ data, nodeId }) {
  const [, sendEvent] = useRcsVisualizerStateMachineAtom();
  const handleClick = () => {
    const { label, postbackData, actionType } = data;
    const reply = {
      actionType,
      label,
      postbackData,
      type: "action" /* ACTION */,
      nodeId
    };
    sendEvent({ type: "reply" /* REPLY */, reply });
  };
  return /* @__PURE__ */ jsxs46(
    "button",
    {
      type: "button",
      className: "flex cursor-pointer items-center justify-between rounded-2xl border border-black bg-gray-200 px-2 py-1.5 text-xs text-black transition-all hover:bg-gray-200/70",
      onClick: handleClick,
      children: [
        /* @__PURE__ */ jsxs46("span", { className: "p-ripple self-center", children: [
          /* @__PURE__ */ jsx74(Icon18, { icon: faReply5, className: "mr-2" }),
          data.label,
          /* @__PURE__ */ jsx74(Ripple, { variant: "default" })
        ] }),
        typeof data.stat !== "undefined" && /* @__PURE__ */ jsx74("div", { className: "pl-2", children: /* @__PURE__ */ jsx74(Badge2, { variant: "info", children: data?.stat }) })
      ]
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/suggestions/suggestion.tsx
import { Fragment as Fragment14, jsx as jsx75, jsxs as jsxs47 } from "react/jsx-runtime";
function Suggestion({ data, nodeId }) {
  const chipClassName = "flex cursor-pointer items-center justify-between rounded-2xl border border-black bg-gray-200 px-2 py-1.5 text-xs text-black transition-all hover:bg-gray-200/70";
  return /* @__PURE__ */ jsxs47(Fragment14, { children: [
    data.actionType === "call" && /* @__PURE__ */ jsx75(
      PhoneActionHandle,
      {
        number: data.number,
        label: data.label,
        className: chipClassName
      }
    ),
    data.actionType === "createCalendarEventAction" && /* @__PURE__ */ jsxs47(CreateCalendarEventActionHandle, { data, className: chipClassName, children: [
      /* @__PURE__ */ jsx75(Icon19, { className: "mr-2", icon: faCalendarAlt5 }),
      data.label
    ] }),
    data.actionType === "reply" && /* @__PURE__ */ jsx75(SuggestionReplyAction, { data, nodeId }),
    data.actionType === "url" && /* @__PURE__ */ jsx75(
      UrlActionHandle,
      {
        href: data.href,
        label: data.label,
        className: chipClassName
      }
    ),
    data.actionType === "viewLocationAction" && /* @__PURE__ */ jsx75(ViewLocationActionHandle, { data, className: chipClassName })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/suggestions/suggestions.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
function Suggestions({ data, nodeId }) {
  const horizontalScrollbarProps = useHorizontalScrollbar();
  return /* @__PURE__ */ jsx76(
    "div",
    {
      className: "flex w-full flex-row-reverse gap-x-2 scroll-smooth whitespace-nowrap p-1.5",
      ...horizontalScrollbarProps,
      children: data.map((suggestion) => /* @__PURE__ */ jsx76(
        Suggestion,
        {
          nodeId,
          data: suggestion
        },
        suggestion.postbackData
      ))
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/carousel/carousel-conversation.tsx
import { Fragment as Fragment15, jsx as jsx77, jsxs as jsxs48 } from "react/jsx-runtime";
function CarouselConversation({
  data,
  nodeId,
  reply,
  refCarousel,
  handleRefCarousel
}) {
  const {
    refCarousel: refCarouselHook,
    handleRefCarousel: handleRefCarouselHook
  } = useCarouselForm({
    length: data.cards.length
  });
  if (typeof refCarousel === "undefined") {
    refCarousel = refCarouselHook;
  }
  if (typeof handleRefCarousel === "undefined") {
    handleRefCarousel = handleRefCarouselHook;
  }
  return /* @__PURE__ */ jsxs48(Fragment15, { children: [
    /* @__PURE__ */ jsx77(
      "div",
      {
        className: "mb-2 flex overflow-x-auto scroll-smooth whitespace-nowrap no-scrollbar",
        ref: refCarousel,
        children: data.cards.map(
          ({ pictureSource, title, description, actions, cardHeight }, index) => /* @__PURE__ */ jsx77(
            "div",
            {
              className: "mr-2 max-w-[180px]",
              onClick: () => handleRefCarousel(index),
              children: /* @__PURE__ */ jsxs48("div", { className: "flex w-[180px] flex-col gap-y-1 overflow-hidden rounded-xl border bg-white", children: [
                /* @__PURE__ */ jsx77(
                  RichCardImage,
                  {
                    pictureSource,
                    cardHeight
                  }
                ),
                /* @__PURE__ */ jsxs48("div", { className: "flex flex-col gap-y-2 whitespace-pre-line break-words p-2 text-xs ", children: [
                  /* @__PURE__ */ jsx77("p", { className: "font-bold", children: title }),
                  /* @__PURE__ */ jsx77("p", { children: description })
                ] }),
                !!actions.length && /* @__PURE__ */ jsx77(Actions2, { nodeId, data: actions })
              ] })
            },
            index
          )
        )
      }
    ),
    reply && /* @__PURE__ */ jsx77(ConversationReply, { reply }),
    !reply && !!data.suggestions?.length && /* @__PURE__ */ jsx77(Suggestions, { nodeId, data: data.suggestions })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/header-carousel-form.tsx
import {
  Button as Button5,
  faPlus as faPlus5,
  faTrashCan as faTrashCan6,
  faTriangleExclamation as faTriangleExclamation2,
  Icon as Icon20,
  IconButton as IconButton7
} from "@oppy/omniv2-ui";
import { jsx as jsx78, jsxs as jsxs49 } from "react/jsx-runtime";
var MAX_RICHCARDS = 10;
function HeaderCarouselForm({
  fields,
  append,
  indexSelected,
  setIndexSelected,
  errors,
  onDelete
}) {
  const handleAddCard = () => {
    {
      append({
        title: "",
        description: "",
        pictureSource: {
          pictureType: PICTURE_TYPE_UPLOAD,
          picture: null
        },
        cardHeight: "MEDIUM" /* MEDIUM */,
        actions: []
      });
      setTimeout(() => setIndexSelected(fields.length), 1);
    }
  };
  return /* @__PURE__ */ jsxs49("div", { className: "flex justify-between pb-2", children: [
    /* @__PURE__ */ jsx78("div", { className: "flex divide-x divide-primary overflow-hidden rounded-lg border border-primary transition-all", children: fields.map((field, index) => /* @__PURE__ */ jsx78("div", { children: /* @__PURE__ */ jsxs49(
      Button5,
      {
        type: "button",
        rounded: false,
        className: "rounded-none border-none",
        variant: indexSelected === index ? "primary" : "outlined",
        onClick: () => setIndexSelected(index),
        children: [
          index + 1,
          errors.cards?.[index] && /* @__PURE__ */ jsx78(
            Icon20,
            {
              className: "pl-1 text-error",
              icon: faTriangleExclamation2
            }
          )
        ]
      }
    ) }, field.id)) }),
    /* @__PURE__ */ jsxs49("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx78(
        IconButton7,
        {
          name: "cards",
          type: "button",
          disabled: fields.length >= MAX_RICHCARDS,
          rounded: true,
          variant: "primary",
          size: "small",
          onClick: () => handleAddCard(),
          iconProps: { icon: faPlus5 }
        }
      ),
      /* @__PURE__ */ jsx78(
        IconButton7,
        {
          onClick: () => onDelete(indexSelected),
          disabled: fields.length < 2,
          rounded: true,
          type: "button",
          variant: "secondary",
          size: "small",
          fullSize: true,
          iconProps: {
            icon: faTrashCan6
          }
        }
      )
    ] })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/use-dialog-form.ts
import { zodResolver } from "@hookform/resolvers/zod";
import { useReactFlow as useReactFlow2 } from "@xyflow/react";
import { useCallback as useCallback11 } from "react";
import { useForm } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/form.schema.ts
import { isValidAspectRatio } from "@oppy/omniv2-utils";
import { z as z5 } from "zod";
var CARD_SCHEMA = z5.object({
  title: z5.string().min(1, "Le titre est obligatoire").max(200, "Le titre ne peut pas d\xE9passer 200 caract\xE8res"),
  description: z5.string().min(1, "La description est obligatoire").max(2e3, "La description ne peut pas d\xE9passer 2000 caract\xE8res"),
  pictureSource: PICTURE_SCHEMA,
  actions: ACTION_SCHEMAS,
  cardHeight: z5.nativeEnum(CardHeightEnum).optional()
}).superRefine((data, ctx) => {
  if (data.pictureSource?.picture && !data.cardHeight) {
    ctx.addIssue({
      path: ["cardHeight"],
      code: z5.ZodIssueCode.custom,
      message: "Ce champ est requis si une image est pr\xE9sente."
    });
  }
  if (data.pictureSource?.pictureType === PICTURE_TYPE_UPLOAD) {
    const { width = 0, height = 0 } = data.pictureSource?.picture?.resolution ?? {};
    if (!!data.pictureSource.picture && !isValidAspectRatio(width, height, [16 / 9, 2 / 1, 7 / 3])) {
      ctx.addIssue({
        path: ["pictureSource.picture.resolution"],
        code: z5.ZodIssueCode.custom,
        message: `Ratios accept\xE9s : 16:9 / 2:1 / 7:3 \u2014 re\xE7u : ${width}x${height}`
      });
    }
  }
});
var SCHEMA = z5.object({
  cards: z5.array(CARD_SCHEMA).min(1).max(10, "10 Cartes au maximum peuvent \xEAtre ajout\xE9es").default([])
});

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/use-dialog-form.ts
function useDialogForm({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { updateNodeData } = useReactFlow2();
  const methods = useForm({
    resolver: zodResolver(SCHEMA, { async: true }),
    mode: "onBlur",
    defaultValues
  });
  const { handleSubmit } = methods;
  const onSubmit = handleSubmit((data) => {
    updateNodeData(nodeId, data);
    handleHide();
  });
  const onCancel = useCallback11(() => {
    handleHide();
  }, [handleHide]);
  return {
    methods,
    onCancel,
    onSubmit
  };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/content-form.tsx
import { jsx as jsx79, jsxs as jsxs50 } from "react/jsx-runtime";
function ContentForm({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { methods, onCancel, onSubmit } = useDialogForm({
    defaultValues: {
      ...defaultValues,
      cards: defaultValues.cards.map((card) => ({
        cardOrientation: "VERTICAL" /* VERTICAL */,
        cardHeight: "MEDIUM" /* MEDIUM */,
        ...card
      }))
    },
    nodeId,
    handleHide
  });
  const {
    control,
    formState: { errors }
  } = methods;
  const { fields, append, remove } = useFieldArray5({
    control,
    name: "cards"
  });
  const { indexCarouselSelected, handleRefCarousel, refCarousel } = useCarouselForm({ length: fields.length });
  const value = useWatch3({
    control
  });
  const onDelete = (index) => {
    if (fields.length > 1) {
      const isLastItem = index === fields.length - 1;
      const newActiveIndex = isLastItem ? Math.max(0, index - 1) : index;
      remove(index);
      handleRefCarousel(newActiveIndex);
    }
  };
  return /* @__PURE__ */ jsx79(FormProvider, { ...methods, children: /* @__PURE__ */ jsxs50("form", { className: "flex h-full", onSubmit, children: [
    /* @__PURE__ */ jsxs50("div", { className: "flex w-full flex-col justify-between gap-3 pr-4 pt-2", children: [
      /* @__PURE__ */ jsxs50("div", { children: [
        /* @__PURE__ */ jsx79(
          HeaderCarouselForm,
          {
            fields,
            append,
            setIndexSelected: handleRefCarousel,
            indexSelected: indexCarouselSelected,
            errors,
            onDelete
          }
        ),
        fields.map(
          (field, index) => indexCarouselSelected === index && /* @__PURE__ */ jsx79(
            CardTabForm,
            {
              indexCarousel: index,
              methods,
              isCarousel: true
            },
            field.id
          )
        )
      ] }),
      /* @__PURE__ */ jsxs50("div", { className: "flex gap-x-3 self-end pb-2", children: [
        /* @__PURE__ */ jsx79(Button6, { variant: "secondary", type: "reset", onClick: onCancel, children: "Annuler" }),
        /* @__PURE__ */ jsx79(Button6, { variant: "primary", type: "submit", children: "Sauvegarder" })
      ] })
    ] }),
    /* @__PURE__ */ jsx79(PreviewCardVisualizer, { children: /* @__PURE__ */ jsx79(
      CarouselConversation,
      {
        data: value,
        nodeId,
        reply: null,
        handleRefCarousel,
        refCarousel
      }
    ) })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/form/dialog-form.tsx
import { jsx as jsx80 } from "react/jsx-runtime";
var DialogFormHeader = function({ nodeId }) {
  return /* @__PURE__ */ jsx80(HeaderNodeTitle, { nodeId, children: /* @__PURE__ */ jsx80(Heading27, { children: "Carrousel" }) });
};
function DialogForm({
  defaultValues,
  nodeId,
  visible,
  setVisible
}) {
  const onHide = useCallback12(() => {
    setVisible(false);
  }, [setVisible]);
  return /* @__PURE__ */ jsx80(
    Dialog,
    {
      header: /* @__PURE__ */ jsx80(DialogFormHeader, { nodeId }),
      visible,
      onHide,
      children: /* @__PURE__ */ jsx80(
        ContentForm,
        {
          defaultValues,
          nodeId,
          handleHide: onHide
        }
      )
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/carousel/carousel-node.tsx
import { Fragment as Fragment16, jsx as jsx81, jsxs as jsxs51 } from "react/jsx-runtime";
function CarouselNode({
  data,
  id: nodeId,
  isConnectable,
  selected
}) {
  const [isDialogOpen, setIsDialogOpen] = useState6(false);
  const [indexCarousel, setIndexCarousel] = useState6(0);
  const { success } = SCHEMA.safeParse(data);
  const handleEdit = (e) => {
    e.preventDefault();
    setIsDialogOpen(true);
  };
  return /* @__PURE__ */ jsx81(NoDroppable, { children: /* @__PURE__ */ jsxs51(
    "div",
    {
      className: cn5(
        "flex flex-col transition border border-gray-400 rounded-md min-w-44 max-w-[300px] min-h-24 shadow-md bg-white",
        {
          "border-black": selected,
          "border-error/70": !success,
          "border-error": !success && selected
        }
      ),
      children: [
        /* @__PURE__ */ jsx81(
          HeaderNode,
          {
            title: "Carrousel",
            onEdit: handleEdit,
            isConnectable,
            nodeId,
            children: /* @__PURE__ */ jsx81(
              DialogForm,
              {
                nodeId,
                visible: isDialogOpen,
                setVisible: setIsDialogOpen,
                defaultValues: data
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs51("div", { className: "grid flex-1 rounded-b-md bg-gray-200", children: [
          /* @__PURE__ */ jsx81(
            RichCardImage,
            {
              pictureSource: data.cards[indexCarousel].pictureSource,
              cardHeight: data.cards[indexCarousel].cardHeight
            }
          ),
          /* @__PURE__ */ jsx81("div", { className: "flex max-w-[300px] flex-col gap-y-2 p-2", children: (!!data.cards[indexCarousel].description || !!data.cards[indexCarousel].actions.length) && /* @__PURE__ */ jsx81(ContentNode, { children: /* @__PURE__ */ jsxs51(Fragment16, { children: [
            !!data.cards[indexCarousel].title && /* @__PURE__ */ jsx81("p", { className: "text-sm font-bold", children: data.cards[indexCarousel].title }),
            !!data.cards[indexCarousel].description && /* @__PURE__ */ jsx81("p", { children: data.cards[indexCarousel].description }),
            !!data.cards[indexCarousel].actions.length && /* @__PURE__ */ jsx81(ActionsHandle, { data: data.cards[indexCarousel].actions })
          ] }) }) }),
          /* @__PURE__ */ jsx81(
            Paginator,
            {
              className: "justify-center",
              first: indexCarousel,
              rows: 1,
              totalRecords: data.cards.length,
              onPageChange: (e) => setIndexCarousel(e.page),
              pageLinkSize: 3,
              templateOption: PaginatorTemplateEnum.TEMPLATE_CARROUSEL
            }
          )
        ] })
      ]
    }
  ) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/media-node.tsx
import { cn as cn8, faPaperclip as faPaperclip2, Icon as Icon22 } from "@oppy/omniv2-ui";
import { useState as useState7 } from "react";

// src/pages/rcs/components/panel-group/panel-group.tsx
import { Panel } from "@oppy/omniv2-ui";
import { Fragment as Fragment17 } from "react";

// src/pages/rcs/components/panel-group/panel-group.style.ts
import { cn as cn6 } from "@oppy/omniv2-ui";
function getPanelStyles(className) {
  return {
    root: {
      className: cn6(
        "m-0 border-none shadow-none rounded-none",
        "last:rounded-b",
        className
      )
    },
    content: {
      className: cn6("p-0 flex flex-col rounded-none", "last:rounded-b")
    },
    header: {
      className: cn6("pb-1 rounded-none pt-3", "first:pt-1")
    },
    title: {
      className: "text-xs text-gray-500 font-thin"
    },
    toggler: {
      className: "rounded-none"
    }
  };
}

// src/pages/rcs/components/panel-group/panel-group.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
function PanelGroup({ panels }) {
  if (!panels.length) return null;
  const panelStyle = getPanelStyles();
  return /* @__PURE__ */ jsx82(Fragment17, { children: panels.map((panelProps, index) => {
    const key = panelProps.key || `panel-${index}`;
    return /* @__PURE__ */ jsx82(Panel, { ...panelProps, pt: panelStyle }, key);
  }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/base/panel-groups.tsx
import { useMemo as useMemo10 } from "react";
import { jsx as jsx83 } from "react/jsx-runtime";
function PanelGroups({
  suggestions,
  freeTextResponses,
  position
}) {
  const panels = useMemo10(
    () => [
      {
        header: "Suggestions",
        children: !suggestions?.length ? null : /* @__PURE__ */ jsx83(SuggestionsHandle, { data: suggestions, position })
      },
      {
        header: "R\xE9ponse libre",
        children: !freeTextResponses?.length ? null : /* @__PURE__ */ jsx83(
          FreeTextResponsesHandle,
          {
            data: freeTextResponses,
            position
          }
        )
      }
    ].filter((panel) => panel.children),
    [freeTextResponses, suggestions, position]
  );
  return /* @__PURE__ */ jsx83(PanelGroup, { panels });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/dialog-form.tsx
import { Dialog as Dialog2, Heading2 as Heading28 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/content-form.tsx
import { Button as Button7 } from "@oppy/omniv2-ui";
import { FormProvider as FormProvider2 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/media/media-conversation.tsx
import { faPaperclip, Icon as Icon21, Message } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/form.schema.ts
import { fileSizeInOctet as fileSizeInOctet2 } from "@oppy/omniv2-utils";
import { z as z6 } from "zod";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/media.constants.ts
var ALLOWED_IMAGE_EXTENSIONS_MEDIA = [".jpg", ".jpeg", ".png"];
var ALLOWED_MEDIA_EXTENSIONS = [
  ".ogx",
  ".pdf",
  ".aac",
  ".mp3",
  ".mp4",
  ".3gp",
  ".jpeg",
  ".jpg",
  ".png",
  ".h263",
  ".m4v",
  ".m4p",
  ".mpeg",
  ".webm"
];
var MEDIA_TYPE_UPLOAD = "upload";
var MEDIA_TYPE_URL = "metadata";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/form.schema.ts
var metadataFileSchema = z6.object({
  name: z6.string().min(1, {
    message: `Le titre est obligatoire`
  }),
  src: z6.string().min(1, {
    message: `L'url est obligatoire`
  })
}).nullable();
var fileSchema = z6.object({
  name: z6.string().min(1, "Le nom du fichier est obligatoire"),
  src: z6.string().url("La source du fichier doit \xEAtre valide"),
  extension: z6.string().min(1, "L'extension du fichier est manquante").refine((ext) => {
    const normalized = ext.toLowerCase().startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico|tiff?)$/i.test(
      normalized
    );
    if (!isImage) return true;
    return ALLOWED_IMAGE_EXTENSIONS_MEDIA.some(
      (allowed) => normalized === allowed.toLowerCase()
    );
  }, "Pour les images, seuls les formats .jpg, .jpeg et .png sont autoris\xE9s"),
  size: z6.number().max(fileSizeInOctet2(100), "Le fichier ne doit pas d\xE9passer 100Mo")
}).nullable();
var MEDIA_SCHEMA = z6.discriminatedUnion("type", [
  z6.object({
    type: z6.literal(MEDIA_TYPE_URL),
    file: metadataFileSchema
  }),
  z6.object({
    type: z6.literal(MEDIA_TYPE_UPLOAD),
    file: fileSchema
  })
]);
var SCHEMA2 = z6.object({
  media: MEDIA_SCHEMA,
  suggestions: SUGGESTIONS_SCHEMA,
  freeTextResponses: FREETEXT_SCHEMA
});

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/media/media-conversation.tsx
import { jsx as jsx84, jsxs as jsxs52 } from "react/jsx-runtime";
function MediaConversation({
  data,
  nodeId,
  reply
}) {
  const { success } = SCHEMA2.safeParse(data);
  if (!success) {
    return /* @__PURE__ */ jsx84(Message, { severity: "error", text: "Le media n'est pas valide" });
  }
  return /* @__PURE__ */ jsxs52("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs52("div", { className: "flex w-full items-center gap-x-2 rounded-xl rounded-bl-none border bg-white px-2", children: [
      /* @__PURE__ */ jsx84("div", { className: "flex size-8 flex-none items-center justify-center rounded-full bg-primary", children: /* @__PURE__ */ jsx84(Icon21, { icon: faPaperclip, className: "m-auto text-white" }) }),
      /* @__PURE__ */ jsx84("div", { className: "my-2 flex w-0 flex-1 flex-col gap-y-2 overflow-hidden p-2 text-sm", children: /* @__PURE__ */ jsx84("p", { className: "line-clamp-2 whitespace-pre-line break-words font-bold", children: data.media.file.name }) })
    ] }),
    reply && /* @__PURE__ */ jsx84(ConversationReply, { reply }),
    !reply && !!data.suggestions.length && /* @__PURE__ */ jsx84(Suggestions, { nodeId, data: data.suggestions })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/message/message-conversation.tsx
import { Message as Message2 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/form.schema.ts
import { z as z7 } from "zod";
var SCHEMA3 = z7.object({
  text: z7.string().min(1, {
    message: "Le texte est obligatoire"
  }),
  suggestions: SUGGESTIONS_SCHEMA,
  freeTextResponses: FREETEXT_SCHEMA
});

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/message/message-conversation.tsx
import { jsx as jsx85, jsxs as jsxs53 } from "react/jsx-runtime";
function MessageConversation({
  data,
  nodeId,
  reply
}) {
  const { success } = SCHEMA3.safeParse(data);
  if (!success) {
    return /* @__PURE__ */ jsx85(Message2, { severity: "error", text: "Le message n'est pas valide" });
  }
  return /* @__PURE__ */ jsxs53("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx85("p", { className: "w-fit max-w-3.5/5 whitespace-pre-wrap break-words rounded-xl rounded-bl-none border bg-white p-2 text-xs", children: data.text }),
    reply && /* @__PURE__ */ jsx85(ConversationReply, { reply }),
    !reply && !!data.suggestions.length && /* @__PURE__ */ jsx85(Suggestions, { nodeId, data: data.suggestions })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/rich-card/rich-card-conversation.tsx
import { cn as cn7 } from "@oppy/omniv2-ui";
import { useMemo as useMemo11 } from "react";
import { jsx as jsx86, jsxs as jsxs54 } from "react/jsx-runtime";
function RichCardConversation({
  data,
  nodeId,
  reply
}) {
  const cardOrientationClassName = useMemo11(
    () => cn7(
      "flex ",
      data.cardOrientation === "HORIZONTAL" /* HORIZONTAL */ ? data.thumbnailImageAlignment === "LEFT" /* LEFT */ ? "flex-row" : "flex-row-reverse" : "flex-col"
    ),
    [data.cardOrientation, data.thumbnailImageAlignment]
  );
  return /* @__PURE__ */ jsxs54("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs54(
      "div",
      {
        className: cn7(
          "flex w-full flex-col gap-y-1 overflow-hidden rounded-xl border bg-white min-h-20",
          cardOrientationClassName
        ),
        children: [
          /* @__PURE__ */ jsx86(
            RichCardImage,
            {
              pictureSource: data.pictureSource,
              cardHeight: data.cardHeight,
              cardOrientation: data.cardOrientation
            }
          ),
          /* @__PURE__ */ jsxs54("div", { className: "flex flex-1 flex-col gap-y-2 whitespace-pre-line break-words p-2 text-xs", children: [
            /* @__PURE__ */ jsx86("p", { className: "font-bold", children: data.title }),
            /* @__PURE__ */ jsx86("p", { children: data.description.replace(/\\n/g, "\n") }),
            !!data.actions.length && /* @__PURE__ */ jsx86(Actions2, { nodeId, data: data.actions })
          ] })
        ]
      }
    ),
    reply && /* @__PURE__ */ jsx86(ConversationReply, { reply }),
    !reply && !!data.suggestions?.length && /* @__PURE__ */ jsx86(Suggestions, { nodeId, data: data.suggestions })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/panels/visualizer-panel/conversation-visualizer/components/renderers/stop-message/stop-message-conversation.tsx
import { jsx as jsx87 } from "react/jsx-runtime";
function StopMessageConversation({
  data
}) {
  return /* @__PURE__ */ jsx87("div", { className: "flex w-full flex-col gap-y-1", children: /* @__PURE__ */ jsx87("p", { className: "max-w-3/5 items-start rounded-xl border bg-white p-2", children: data.stopMessage }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/tab-form.tsx
import { Tab as Tab2 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/medias/media-form.tsx
import { FileUploaderForm, SwitchForm as SwitchForm4 } from "@oppy/omniv2-form";
import { HelperText } from "@oppy/omniv2-ui";
import { useCallback as useCallback13 } from "react";
import { useWatch as useWatch4 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/media-linked-form.tsx
import { InputForm as InputForm11 } from "@oppy/omniv2-form";
import React from "react";
import { jsx as jsx88, jsxs as jsxs55 } from "react/jsx-runtime";
function MediaLinkedForm({
  control,
  nameSrc,
  nameTitle
}) {
  return /* @__PURE__ */ jsxs55(React.Fragment, { children: [
    /* @__PURE__ */ jsx88(InputForm11, { control, name: nameTitle, placeholder: "Titre" }),
    /* @__PURE__ */ jsx88("div", { className: "pt-4", children: /* @__PURE__ */ jsx88(InputForm11, { control, name: nameSrc, placeholder: "URL" }) })
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/forms/medias/media-form.tsx
import { jsx as jsx89, jsxs as jsxs56 } from "react/jsx-runtime";
function MediaForm({ control, setValue }) {
  const value = useWatch4({
    control
  });
  const { media } = value;
  const handleMediaTypeChange = useCallback13(() => {
    setValue("media.file", {
      name: "",
      src: "",
      size: 0,
      extension: ""
    });
  }, [setValue]);
  return /* @__PURE__ */ jsxs56("div", { className: "space-y-2 pt-2", children: [
    /* @__PURE__ */ jsx89("div", { className: "pb-2", children: /* @__PURE__ */ jsx89(
      SwitchForm4,
      {
        control,
        name: "media.type",
        label: "Via URL",
        condition: [MEDIA_TYPE_URL, MEDIA_TYPE_UPLOAD],
        onChange: handleMediaTypeChange
      }
    ) }),
    media?.type === MEDIA_TYPE_URL ? /* @__PURE__ */ jsx89(
      MediaLinkedForm,
      {
        control,
        nameSrc: "media.file.src",
        nameTitle: "media.file.name"
      }
    ) : /* @__PURE__ */ jsx89(
      FileUploaderForm,
      {
        control,
        name: "media.file",
        placeholder: "Fichier",
        acceptedFiles: ALLOWED_MEDIA_EXTENSIONS.join(",")
      }
    ),
    /* @__PURE__ */ jsx89(
      HelperText,
      {
        text: `Formats autoris\xE9s : *${ALLOWED_MEDIA_EXTENSIONS.join(", *")} ou {{metaData.fileUrl}}`
      }
    )
  ] });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/tab-form.tsx
import { jsx as jsx90 } from "react/jsx-runtime";
var tabs2 = ({ control, setValue, errors, watch, controlName }) => [
  {
    header: /* @__PURE__ */ jsx90(header_tab_form_default, { title: "contenu", errors, fieldNames: ["media"] }),
    children: /* @__PURE__ */ jsx90(MediaForm, { control, setValue })
  },
  {
    header: /* @__PURE__ */ jsx90(
      header_tab_form_default,
      {
        title: "suggestions",
        errors,
        fieldNames: ["suggestions"]
      }
    ),
    children: /* @__PURE__ */ jsx90(
      SuggestionsForm,
      {
        control,
        controlName: `${controlName}suggestions`,
        watch
      }
    )
  },
  {
    header: /* @__PURE__ */ jsx90(
      header_tab_form_default,
      {
        title: "R\xE9ponse libre",
        errors,
        fieldNames: ["freeTextResponses"]
      }
    ),
    children: /* @__PURE__ */ jsx90(
      FreeTextsForm,
      {
        watch,
        control
      }
    )
  }
];
function MediaTabForm({ methods }) {
  const {
    control,
    setValue,
    formState: { errors },
    watch
  } = methods;
  const controlName = "";
  const tabOption = {
    control,
    setValue,
    errors,
    watch,
    controlName
  };
  return /* @__PURE__ */ jsx90(Tab2, { items: tabs2(tabOption) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/use-dialog-form.ts
import { zodResolver as zodResolver2 } from "@hookform/resolvers/zod";
import { useReactFlow as useReactFlow3 } from "@xyflow/react";
import { useCallback as useCallback14 } from "react";
import { useForm as useForm2 } from "react-hook-form";
function useDialogForm2({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { updateNodeData } = useReactFlow3();
  const methods = useForm2({
    resolver: zodResolver2(SCHEMA2, { async: true }),
    mode: "onBlur",
    defaultValues
  });
  const { handleSubmit } = methods;
  const onSubmit = handleSubmit((data) => {
    updateNodeData(nodeId, data);
    handleHide();
  });
  const onCancel = useCallback14(() => {
    handleHide();
  }, [handleHide]);
  return {
    methods,
    onCancel,
    onSubmit
  };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/content-form.tsx
import { jsx as jsx91, jsxs as jsxs57 } from "react/jsx-runtime";
function ContentForm2({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { methods, onCancel, onSubmit } = useDialogForm2({
    defaultValues,
    nodeId,
    handleHide
  });
  const { watch } = methods;
  const value = watch();
  return /* @__PURE__ */ jsx91(FormProvider2, { ...methods, children: /* @__PURE__ */ jsxs57("form", { className: "flex h-full", onSubmit, children: [
    /* @__PURE__ */ jsxs57("div", { className: "flex w-full flex-col justify-between gap-3 pr-4 pt-2", children: [
      /* @__PURE__ */ jsx91(MediaTabForm, { methods }),
      /* @__PURE__ */ jsxs57("div", { className: "flex justify-end gap-x-3 self-end", children: [
        /* @__PURE__ */ jsx91(Button7, { variant: "secondary", type: "reset", onClick: onCancel, children: "Annuler" }),
        /* @__PURE__ */ jsx91(Button7, { variant: "primary", type: "submit", children: "Sauvegarder" })
      ] })
    ] }),
    /* @__PURE__ */ jsx91(PreviewCardVisualizer, { children: /* @__PURE__ */ jsx91(
      MediaConversation,
      {
        data: value,
        nodeId,
        reply: null
      }
    ) })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/form/dialog-form.tsx
import { jsx as jsx92 } from "react/jsx-runtime";
var DialogFormHeader2 = function({ nodeId }) {
  return /* @__PURE__ */ jsx92(HeaderNodeTitle, { nodeId, children: /* @__PURE__ */ jsx92(Heading28, { children: "Media" }) });
};
function DialogForm2({
  data,
  nodeId,
  visible,
  setVisible
}) {
  const onHide = () => setVisible(false);
  return /* @__PURE__ */ jsx92(
    Dialog2,
    {
      header: /* @__PURE__ */ jsx92(DialogFormHeader2, { nodeId }),
      visible,
      onHide,
      size: "w-[50vw]",
      children: /* @__PURE__ */ jsx92(ContentForm2, { defaultValues: data, nodeId, handleHide: onHide })
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/media/media-node.tsx
import { jsx as jsx93, jsxs as jsxs58 } from "react/jsx-runtime";
function MediaNode({
  id: nodeId,
  isConnectable,
  data,
  selected
}) {
  const [isDialogOpen, setIsDialogOpen] = useState7(false);
  const { success } = SCHEMA2.safeParse(data);
  const handleEdit = (e) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  };
  return /* @__PURE__ */ jsx93(NoDroppable, { children: /* @__PURE__ */ jsxs58(
    "div",
    {
      className: cn8(
        "flex flex-col transition border border-gray-400 rounded-md min-w-44 max-w-[300px] min-h-24 shadow-md",
        {
          "border-black": selected,
          "border-error/70": !success,
          "border-error": !success && selected
        }
      ),
      children: [
        /* @__PURE__ */ jsx93(
          HeaderNode,
          {
            title: "Media",
            onEdit: handleEdit,
            isConnectable,
            nodeId,
            children: /* @__PURE__ */ jsx93(
              DialogForm2,
              {
                nodeId,
                visible: isDialogOpen,
                setVisible: setIsDialogOpen,
                data
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs58("div", { className: "grid flex-1 rounded-b-md bg-gray-200", children: [
          /* @__PURE__ */ jsx93("div", { className: "flex max-w-[300px] flex-col gap-y-2 p-2", children: !!data.media && data.media.file.name && /* @__PURE__ */ jsx93(ContentNode, { children: /* @__PURE__ */ jsxs58("div", { className: "flex w-full items-center", children: [
            /* @__PURE__ */ jsx93("div", { className: "ml-2 flex size-8 flex-none items-center justify-center rounded-full bg-primary", children: /* @__PURE__ */ jsx93(Icon22, { icon: faPaperclip2, className: "m-auto text-white" }) }),
            /* @__PURE__ */ jsx93("div", { className: "flex flex-initial flex-col gap-y-2 p-2 text-sm", children: /* @__PURE__ */ jsx93("p", { className: "font-bold", children: data.media.file.name }) })
          ] }) }) }),
          /* @__PURE__ */ jsx93(PanelGroups, { ...data })
        ] })
      ]
    }
  ) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/message-node.tsx
import { cn as cn9 } from "@oppy/omniv2-ui";
import { useState as useState8 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/dialog-form.tsx
import { Dialog as Dialog3, Heading2 as Heading29 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/content-form.tsx
import { Button as Button8 } from "@oppy/omniv2-ui";
import { FormProvider as FormProvider3 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/tab-form.tsx
import { TextAreaForm as TextAreaForm3 } from "@oppy/omniv2-form";
import { Tab as Tab3 } from "@oppy/omniv2-ui";
import { jsx as jsx94 } from "react/jsx-runtime";
var tabs3 = ({ control, controlName, errors, watch }) => [
  {
    header: /* @__PURE__ */ jsx94(header_tab_form_default, { title: "contenu", errors, fieldNames: ["text"] }),
    children: /* @__PURE__ */ jsx94(
      TextAreaForm3,
      {
        control,
        name: "text",
        placeholder: "Contenu",
        maxLength: 3072,
        rows: 8,
        hasEmojiPicker: true
      }
    )
  },
  {
    header: /* @__PURE__ */ jsx94(
      header_tab_form_default,
      {
        title: "suggestions",
        errors,
        fieldNames: ["suggestions"]
      }
    ),
    children: /* @__PURE__ */ jsx94(
      SuggestionsForm,
      {
        control,
        controlName: `${controlName}suggestions`,
        watch
      }
    )
  },
  {
    header: /* @__PURE__ */ jsx94(
      header_tab_form_default,
      {
        title: "R\xE9ponse libre",
        errors,
        fieldNames: ["freeTextResponses"]
      }
    ),
    children: /* @__PURE__ */ jsx94(
      FreeTextsForm,
      {
        watch,
        control
      }
    )
  }
];
function MessageTabForm({ methods }) {
  const {
    control,
    formState: { errors },
    watch
  } = methods;
  const controlName = "";
  const tabOption = {
    control,
    errors,
    watch,
    controlName
  };
  return /* @__PURE__ */ jsx94(Tab3, { items: tabs3(tabOption) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/use-dialog-form.ts
import { zodResolver as zodResolver3 } from "@hookform/resolvers/zod";
import { useReactFlow as useReactFlow4 } from "@xyflow/react";
import { useCallback as useCallback15 } from "react";
import { useForm as useForm3 } from "react-hook-form";
function useDialogForm3({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { updateNodeData } = useReactFlow4();
  const methods = useForm3({
    resolver: zodResolver3(SCHEMA3, { async: true }),
    mode: "onBlur",
    defaultValues
  });
  const { handleSubmit } = methods;
  const onSubmit = handleSubmit((data) => {
    updateNodeData(nodeId, data);
    handleHide();
  });
  const onCancel = useCallback15(() => {
    handleHide();
  }, [handleHide]);
  return {
    methods,
    onCancel,
    onSubmit
  };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/content-form.tsx
import { jsx as jsx95, jsxs as jsxs59 } from "react/jsx-runtime";
function ContentForm3({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { methods, onCancel, onSubmit } = useDialogForm3({
    defaultValues,
    nodeId,
    handleHide
  });
  const { watch } = methods;
  const value = watch();
  return /* @__PURE__ */ jsx95(FormProvider3, { ...methods, children: /* @__PURE__ */ jsxs59("form", { className: "flex h-full", onSubmit, children: [
    /* @__PURE__ */ jsxs59("div", { className: "flex w-full flex-col justify-between gap-3 pr-4 pt-2", children: [
      /* @__PURE__ */ jsx95(MessageTabForm, { methods }),
      /* @__PURE__ */ jsxs59("div", { className: "flex justify-end gap-x-3 self-end", children: [
        /* @__PURE__ */ jsx95(Button8, { variant: "secondary", type: "reset", onClick: onCancel, children: "Annuler" }),
        /* @__PURE__ */ jsx95(Button8, { variant: "primary", type: "submit", children: "Sauvegarder" })
      ] })
    ] }),
    /* @__PURE__ */ jsx95(PreviewCardVisualizer, { children: /* @__PURE__ */ jsx95(
      MessageConversation,
      {
        data: value,
        nodeId,
        reply: null
      }
    ) })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/form/dialog-form.tsx
import { jsx as jsx96 } from "react/jsx-runtime";
var DialogFormHeader3 = function({ nodeId }) {
  return /* @__PURE__ */ jsx96(HeaderNodeTitle, { nodeId, children: /* @__PURE__ */ jsx96(Heading29, { children: "Message Texte" }) });
};
function DialogForm3({
  data,
  nodeId,
  visible,
  setVisible
}) {
  const onHide = () => setVisible(false);
  return /* @__PURE__ */ jsx96(
    Dialog3,
    {
      header: /* @__PURE__ */ jsx96(DialogFormHeader3, { nodeId }),
      visible,
      onHide,
      children: /* @__PURE__ */ jsx96(ContentForm3, { defaultValues: data, nodeId, handleHide: onHide })
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/message/message-node.tsx
import { jsx as jsx97, jsxs as jsxs60 } from "react/jsx-runtime";
function MessageNode({
  id: nodeId,
  isConnectable,
  data,
  selected
}) {
  const [isDialogOpen, setIsDialogOpen] = useState8(false);
  const { success } = SCHEMA3.safeParse(data);
  const handleEdit = (e) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  };
  return /* @__PURE__ */ jsx97(NoDroppable, { children: /* @__PURE__ */ jsxs60(
    "div",
    {
      className: cn9(
        "flex flex-col transition border border-gray-400 rounded-md min-w-44 max-w-[300px] min-h-24 shadow-md",
        {
          "border-black": selected,
          "border-error/70": !success,
          "border-error": !success && selected
        }
      ),
      children: [
        /* @__PURE__ */ jsx97(
          HeaderNode,
          {
            title: "Message Texte",
            onEdit: handleEdit,
            isConnectable,
            nodeId,
            children: /* @__PURE__ */ jsx97(
              DialogForm3,
              {
                nodeId,
                visible: isDialogOpen,
                setVisible: setIsDialogOpen,
                data
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs60("div", { className: "grid flex-1 rounded-b-md bg-gray-200", children: [
          /* @__PURE__ */ jsx97("div", { className: "flex max-w-[300px] flex-col gap-y-2 p-2", children: !!data.text && /* @__PURE__ */ jsx97(ContentNode, { children: data.text }) }),
          /* @__PURE__ */ jsx97(PanelGroups, { ...data })
        ] })
      ]
    }
  ) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/rich-card-node.tsx
import { cn as cn10 } from "@oppy/omniv2-ui";
import { Position as Position6 } from "@xyflow/react";
import { useMemo as useMemo12, useState as useState9 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/dialog-form.tsx
import { Dialog as Dialog4, Heading2 as Heading210 } from "@oppy/omniv2-ui";
import { useCallback as useCallback17 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/content-form.tsx
import { Button as Button9 } from "@oppy/omniv2-ui";
import { FormProvider as FormProvider4 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/use-dialog-form.ts
import { zodResolver as zodResolver4 } from "@hookform/resolvers/zod";
import { useReactFlow as useReactFlow5 } from "@xyflow/react";
import { useCallback as useCallback16 } from "react";
import { useForm as useForm4 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/form.schema.ts
import { isValidAspectRatio as isValidAspectRatio2 } from "@oppy/omniv2-utils";
import { z as z8 } from "zod";
var SCHEMA4 = z8.object({
  title: z8.string().max(200, "Le titre ne peut pas d\xE9passer 200 caract\xE8res"),
  description: z8.string().max(2e3, "La description ne peut pas d\xE9passer 2000 caract\xE8res"),
  pictureSource: PICTURE_SCHEMA,
  actions: ACTION_SCHEMAS,
  suggestions: SUGGESTIONS_SCHEMA,
  freeTextResponses: FREETEXT_SCHEMA,
  cardHeight: z8.nativeEnum(CardHeightEnum).optional(),
  cardOrientation: z8.nativeEnum(CardOrientationEnum).optional(),
  thumbnailImageAlignment: z8.nativeEnum(CardImageAlignmentEnum).optional()
}).superRefine((data, ctx) => {
  if (data.pictureSource?.picture && !data.cardOrientation) {
    ctx.addIssue({
      path: ["cardOrientation"],
      code: z8.ZodIssueCode.custom,
      message: "Ce champ est requis si une image est pr\xE9sente."
    });
  }
  if (data.pictureSource?.picture && data.cardOrientation === "VERTICAL" /* VERTICAL */ && !data.cardHeight) {
    ctx.addIssue({
      path: ["cardHeight"],
      code: z8.ZodIssueCode.custom,
      message: "Ce champ est requis si une image est pr\xE9sente."
    });
  }
  if (data.pictureSource?.picture && data.cardOrientation === "HORIZONTAL" /* HORIZONTAL */ && !data.thumbnailImageAlignment) {
    ctx.addIssue({
      path: ["thumbnailImageAlignment"],
      code: z8.ZodIssueCode.custom,
      message: "Ce champ est requis si une image est pr\xE9sente."
    });
  }
  if (data.pictureSource?.pictureType === PICTURE_TYPE_UPLOAD) {
    const { width = 0, height = 0 } = data.pictureSource?.picture?.resolution ?? {};
    if (!!data.cardOrientation && data.cardOrientation === "HORIZONTAL" /* HORIZONTAL */ && !isValidAspectRatio2(width, height, [3 / 4])) {
      ctx.addIssue({
        path: ["pictureSource.picture.resolution"],
        code: z8.ZodIssueCode.custom,
        message: `Ratios accept\xE9s : 3:4 \u2014 re\xE7u : ${width}x${height}`
      });
    }
    if (!!data.cardOrientation && data.cardOrientation === "VERTICAL" /* VERTICAL */ && !isValidAspectRatio2(width, height, [16 / 9, 2 / 1, 7 / 3])) {
      ctx.addIssue({
        path: ["pictureSource.picture.resolution"],
        code: z8.ZodIssueCode.custom,
        message: `Ratios accept\xE9s : 16:9 / 2:1 / 7:3 \u2014 re\xE7u : ${width}x${height}`
      });
    }
  }
});

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/use-dialog-form.ts
function useDialogForm4({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { updateNodeData } = useReactFlow5();
  const methods = useForm4({
    resolver: zodResolver4(SCHEMA4, {
      async: true
    }),
    mode: "onBlur",
    defaultValues
  });
  const { handleSubmit } = methods;
  const onSubmit = handleSubmit((data) => {
    updateNodeData(nodeId, data);
    handleHide();
  });
  const onCancel = useCallback16(() => {
    handleHide();
  }, [handleHide]);
  return {
    methods,
    onCancel,
    onSubmit
  };
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/content-form.tsx
import { jsx as jsx98, jsxs as jsxs61 } from "react/jsx-runtime";
function ContentForm4({
  defaultValues,
  nodeId,
  handleHide
}) {
  const { methods, onCancel, onSubmit } = useDialogForm4({
    defaultValues: {
      cardOrientation: "VERTICAL" /* VERTICAL */,
      cardHeight: "MEDIUM" /* MEDIUM */,
      ...defaultValues
    },
    nodeId,
    handleHide
  });
  const { watch } = methods;
  const value = watch();
  return /* @__PURE__ */ jsx98(FormProvider4, { ...methods, children: /* @__PURE__ */ jsxs61("form", { className: "flex h-full", onSubmit, children: [
    /* @__PURE__ */ jsxs61("div", { className: "flex w-full flex-col justify-between gap-3 pr-4 pt-2", children: [
      /* @__PURE__ */ jsx98(CardTabForm, { methods }),
      /* @__PURE__ */ jsxs61("div", { className: "flex justify-end gap-x-3 self-end pb-2", children: [
        /* @__PURE__ */ jsx98(Button9, { variant: "secondary", type: "reset", onClick: onCancel, children: "Annuler" }),
        /* @__PURE__ */ jsx98(Button9, { variant: "primary", type: "submit", children: "Sauvegarder" })
      ] })
    ] }),
    /* @__PURE__ */ jsx98(PreviewCardVisualizer, { children: /* @__PURE__ */ jsx98(
      RichCardConversation,
      {
        data: value,
        nodeId,
        reply: null
      }
    ) })
  ] }) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/form/dialog-form.tsx
import { jsx as jsx99 } from "react/jsx-runtime";
var DialogFormHeader4 = function({ nodeId }) {
  return /* @__PURE__ */ jsx99(HeaderNodeTitle, { nodeId, children: /* @__PURE__ */ jsx99(Heading210, { children: "Carte enrichie" }) });
};
function DialogForm4({
  defaultValues,
  nodeId,
  visible,
  setVisible
}) {
  const onHide = useCallback17(() => {
    setVisible(false);
  }, [setVisible]);
  return /* @__PURE__ */ jsx99(
    Dialog4,
    {
      header: /* @__PURE__ */ jsx99(DialogFormHeader4, { nodeId }),
      visible,
      onHide,
      children: /* @__PURE__ */ jsx99(
        ContentForm4,
        {
          defaultValues,
          nodeId,
          handleHide: onHide
        }
      )
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/rich-card/rich-card-node.tsx
import { Fragment as Fragment18, jsx as jsx100, jsxs as jsxs62 } from "react/jsx-runtime";
function RichCardNode({
  data,
  id: nodeId,
  isConnectable,
  selected
}) {
  const [isDialogOpen, setIsDialogOpen] = useState9(false);
  const { success } = SCHEMA4.safeParse(data);
  const handleEdit = (e) => {
    e.preventDefault();
    setIsDialogOpen(true);
  };
  const handlePosition = useMemo12(
    () => data.thumbnailImageAlignment === "RIGHT" /* RIGHT */ ? Position6.Left : Position6.Right,
    [data.thumbnailImageAlignment]
  );
  const cardOrientationClassName = useMemo12(
    () => cn10(
      "flex ",
      data.cardOrientation === "HORIZONTAL" /* HORIZONTAL */ ? data.thumbnailImageAlignment === "LEFT" /* LEFT */ ? "flex-row" : "flex-row-reverse" : "flex-col"
    ),
    [data.cardOrientation, data.thumbnailImageAlignment]
  );
  return /* @__PURE__ */ jsx100(NoDroppable, { children: /* @__PURE__ */ jsxs62(
    "div",
    {
      className: cn10(
        "flex flex-col transition border border-gray-400 rounded-md min-w-44 max-w-[300px] min-h-24 shadow-md bg-white",
        {
          "border-black": selected,
          "border-error/70": !success,
          "border-error": !success && selected
        }
      ),
      children: [
        /* @__PURE__ */ jsx100(
          HeaderNode,
          {
            title: "Carte Enrichie",
            onEdit: handleEdit,
            isConnectable,
            nodeId,
            children: /* @__PURE__ */ jsx100(
              DialogForm4,
              {
                nodeId,
                visible: isDialogOpen,
                setVisible: setIsDialogOpen,
                defaultValues: data
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs62(
          "div",
          {
            className: cn10(
              "grid flex-1 rounded-b-md bg-gray-200",
              cardOrientationClassName
            ),
            children: [
              /* @__PURE__ */ jsx100(
                RichCardImage,
                {
                  pictureSource: data.pictureSource,
                  cardHeight: data.cardHeight,
                  cardOrientation: data.cardOrientation
                }
              ),
              /* @__PURE__ */ jsxs62("div", { className: "flex max-w-[300px] flex-1 flex-col rounded-b-md", children: [
                (!!data.description || !!data.actions.length) && /* @__PURE__ */ jsx100("div", { className: "flex flex-col gap-y-2 rounded-b-md p-2", children: /* @__PURE__ */ jsx100(ContentNode, { children: /* @__PURE__ */ jsxs62(Fragment18, { children: [
                  !!data.title && /* @__PURE__ */ jsx100("p", { className: "text-xs font-bold", children: data.title }),
                  !!data.description && /* @__PURE__ */ jsx100("p", { children: data.description }),
                  !!data.actions.length && /* @__PURE__ */ jsx100(
                    ActionsHandle,
                    {
                      data: data.actions,
                      position: handlePosition
                    }
                  )
                ] }) }) }),
                /* @__PURE__ */ jsx100(PanelGroups, { ...data, position: handlePosition })
              ] })
            ]
          }
        )
      ]
    }
  ) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/start-node.tsx
import { cn as cn11 } from "@oppy/omniv2-ui";
import { Position as Position7 } from "@xyflow/react";
import { jsx as jsx101, jsxs as jsxs63 } from "react/jsx-runtime";
function StartNode({
  data,
  selected
}) {
  const { label } = data;
  return /* @__PURE__ */ jsx101(NoDroppable, { children: /* @__PURE__ */ jsxs63(
    "div",
    {
      className: cn11(
        "transition bg-primary flex items-center justify-center rounded w-24 h-24 shadow-md",
        { "border border-black": selected }
      ),
      children: [
        /* @__PURE__ */ jsx101("span", { className: "font-bold", children: label }),
        /* @__PURE__ */ jsx101(ConnectionLimitHandle, { id: "a", position: Position7.Bottom }),
        /* @__PURE__ */ jsx101(ConnectionLimitHandle, { id: "b", position: Position7.Right })
      ]
    }
  ) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/stop-node.tsx
import { cn as cn12 } from "@oppy/omniv2-ui";
import { Position as Position8 } from "@xyflow/react";
import { useState as useState10 } from "react";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/form/dialog-form.tsx
import { Dialog as Dialog5, Heading2 as Heading211 } from "@oppy/omniv2-ui";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/form/content-form.tsx
import { zodResolver as zodResolver5 } from "@hookform/resolvers/zod";
import { TextAreaForm as TextAreaForm4 } from "@oppy/omniv2-form";
import { Button as Button10 } from "@oppy/omniv2-ui";
import { useReactFlow as useReactFlow6 } from "@xyflow/react";
import { useForm as useForm5 } from "react-hook-form";

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/form/form.schema.ts
import { z as z9 } from "zod";
var SCHEMA5 = z9.object({
  stopMessage: z9.string().min(1, {
    message: "Le message de d\xE9sinscription est obligatoire"
  })
});

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/form/content-form.tsx
import { jsx as jsx102, jsxs as jsxs64 } from "react/jsx-runtime";
function ContentForm5({ defaultValues, nodeId, onHide }) {
  const { updateNodeData } = useReactFlow6();
  const { control, handleSubmit } = useForm5({
    resolver: zodResolver5(SCHEMA5, { async: true }),
    mode: "onBlur",
    defaultValues
  });
  const handleCancel = () => {
    onHide();
  };
  const onSubmit = (data) => {
    updateNodeData(nodeId, data);
    onHide();
  };
  return /* @__PURE__ */ jsxs64(
    "form",
    {
      className: "flex h-full flex-col justify-between",
      onSubmit: handleSubmit(onSubmit),
      children: [
        /* @__PURE__ */ jsx102("div", { className: "pt-2", children: /* @__PURE__ */ jsx102(
          TextAreaForm4,
          {
            control,
            name: "stopMessage",
            placeholder: "Contenu",
            hasEmojiPicker: true
          }
        ) }),
        /* @__PURE__ */ jsxs64("div", { className: "flex justify-end gap-x-3 self-end", children: [
          /* @__PURE__ */ jsx102(Button10, { variant: "secondary", type: "reset", onClick: handleCancel, children: "Annuler" }),
          /* @__PURE__ */ jsx102(Button10, { variant: "primary", type: "submit", children: "Sauvegarder" })
        ] })
      ]
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/form/dialog-form.tsx
import { jsx as jsx103 } from "react/jsx-runtime";
var DialogFormHeader5 = function({ nodeId }) {
  return /* @__PURE__ */ jsx103(HeaderNodeTitle, { nodeId, children: /* @__PURE__ */ jsx103(Heading211, { children: "Message de d\xE9sinscription" }) });
};
function DialogForm5({
  data,
  nodeId,
  visible,
  setVisible
}) {
  const onHide = () => setVisible(false);
  return /* @__PURE__ */ jsx103(
    Dialog5,
    {
      header: /* @__PURE__ */ jsx103(DialogFormHeader5, { nodeId }),
      visible,
      onHide,
      children: /* @__PURE__ */ jsx103(ContentForm5, { defaultValues: data, nodeId, onHide })
    }
  );
}

// src/pages/rcs/components/rcs-wizard-form/workflow/components/nodes/stop/stop-node.tsx
import { jsx as jsx104, jsxs as jsxs65 } from "react/jsx-runtime";
function StopNode({
  id: nodeId,
  isConnectable,
  data,
  selected
}) {
  const [isDialogOpen, setIsDialogOpen] = useState10(false);
  const handleEdit = (e) => {
    e.stopPropagation();
    setIsDialogOpen(true);
  };
  return /* @__PURE__ */ jsx104(NoDroppable, { children: /* @__PURE__ */ jsxs65(
    "div",
    {
      className: cn12(
        "transition border border-gray-400 rounded-md min-w-44 max-w-[300px] shadow-md",
        { "border-black": selected }
      ),
      children: [
        /* @__PURE__ */ jsx104(
          HeaderNode,
          {
            title: "Message de d\xE9sinscription",
            onEdit: handleEdit,
            sentHandle: false,
            isConnectable,
            nodeId,
            children: /* @__PURE__ */ jsx104(
              DialogForm5,
              {
                nodeId,
                visible: isDialogOpen,
                setVisible: setIsDialogOpen,
                data
              }
            )
          }
        ),
        /* @__PURE__ */ jsx104("div", { className: "rounded-b-md bg-gray-200 p-2", children: /* @__PURE__ */ jsx104(ContentNode, { children: data.stopMessage }) }),
        /* @__PURE__ */ jsx104(
          BaseHandle,
          {
            id: "a",
            type: "target",
            position: Position8.Top,
            isConnectable,
            style: { width: "10px", height: "10px" }
          }
        )
      ]
    }
  ) });
}

// src/pages/rcs/components/rcs-wizard-form/workflow/rcs-workflow.constants.ts
var NODE_TYPES = {
  message: MessageNode,
  richCard: RichCardNode,
  media: MediaNode,
  carousel: CarouselNode,
  startNode: StartNode,
  stopMessage: StopNode
};
var ON_SENT_HANDLE_ID = "on-sent-handle";
var ON_SENT_EDGE_TYPE = "ON_SENT";
var ON_ANSWER_EDGE_TYPE = "ON_ANSWER";
var EDGE_TYPES = {
  default: CustomEdge,
  [ON_ANSWER_EDGE_TYPE]: CustomEdge,
  [ON_SENT_EDGE_TYPE]: SentEventEdge
};

// src/lib/core/store/rcs-visualizer-state-machine/utils/add-node-on-sent.ts
var getNodeId = (convo) => convo.nodeId;
var checkRelevantEdge = (conversationNodeIds, isFreeTextResponse, lastReply) => (edge) => {
  const isStandardCase = edge.type === ON_SENT_EDGE_TYPE && conversationNodeIds.has(edge.source) && !conversationNodeIds.has(edge.target);
  const isFreeTextCase = isFreeTextResponse && lastReply.postbackData === edge.id && !conversationNodeIds.has(edge.target);
  return isStandardCase || isFreeTextCase;
};
var createConversation = (edge) => ({
  nodeId: edge.target,
  reply: null
});
function addNodeOnSent(conversations, flow) {
  const { edges } = flow;
  const conversationNodeIds = new Set(conversations.map(getNodeId));
  const lastConversation = conversations.length > 0 ? conversations[conversations.length - 1] : null;
  const lastReply = lastConversation?.reply;
  const isFreeTextResponse = lastReply?.type === "freeTextResponse" /* FREETEXTRESPONSE */;
  const relevantEdges = edges.filter(
    checkRelevantEdge(conversationNodeIds, isFreeTextResponse, lastReply)
  );
  if (!relevantEdges.length) {
    return conversations;
  }
  return [...conversations, ...relevantEdges.map(createConversation)];
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/get-conversation-by-node-id.ts
function getConversationByNodeId(conversations, nodeId) {
  return conversations.find((conversation) => conversation.nodeId === nodeId);
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/get-conversation-index-by-node-id.ts
function getConversationIndexByNodeId(conversations, nodeId) {
  return conversations.findIndex(
    (conversation) => conversation.nodeId === nodeId
  );
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/update-conversation.ts
function updateConversation(conversations, conversation, index) {
  const newConversations = [...conversations];
  newConversations[index] = conversation;
  return newConversations;
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/add-reply.ts
function addReply(conversations, reply) {
  const conversation = getConversationByNodeId(conversations, reply.nodeId);
  const conversationIndex = getConversationIndexByNodeId(
    conversations,
    reply.nodeId
  );
  if (!conversation || conversationIndex === -1) return conversations;
  const { nodeId } = conversation;
  const updatedConversation = {
    nodeId,
    reply
  };
  return updateConversation(
    conversations,
    updatedConversation,
    conversationIndex
  );
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/process-free-text-response.ts
var findActiveRichCardNode = (flow, conversations) => {
  if (conversations.length === 0) return null;
  const lastConversation = conversations[conversations.length - 1];
  const lastNodeId = lastConversation.nodeId;
  const activeNode = flow.nodes.find((node) => node.id === lastNodeId);
  if (activeNode) {
    return activeNode;
  }
  return null;
};
var createFreeTextReply = (typedMessage, nodeId, postbackData = "") => ({
  label: typedMessage,
  type: "freeTextResponse" /* FREETEXTRESPONSE */,
  nodeId,
  postbackData
});
var handleEqualsToMode = (conversations, typedMessage, freeTextData, nodeId) => {
  const matchingMatcher = freeTextData.matchers.find(
    (matcher) => matcher.value.toLocaleLowerCase() === typedMessage
  );
  return addReply(
    conversations,
    createFreeTextReply(
      typedMessage,
      nodeId,
      matchingMatcher?.postbackData || ""
    )
  );
};
var handleDifferentFromMode = (conversations, typedMessage, freeTextData, nodeId) => {
  const isDifferent = freeTextData.matchers[0].value.toLocaleLowerCase() !== typedMessage;
  return addReply(
    conversations,
    createFreeTextReply(
      typedMessage,
      nodeId,
      isDifferent ? freeTextData.matchers[0].postbackData : ""
    )
  );
};
var handleFreeResponseMode = (conversations, typedMessage, freeTextData, nodeId) => {
  const postbackData = freeTextData.matchers?.[0].postbackData || "";
  return addReply(
    conversations,
    createFreeTextReply(typedMessage, nodeId, postbackData)
  );
};
var handleRegexMode = (conversations, typedMessage, freeTextData, nodeId) => {
  const matchingMatcher = freeTextData.matchers.find((matcher) => {
    try {
      const regex = new RegExp(matcher.value);
      return regex.test(typedMessage);
    } catch {
      return false;
    }
  });
  return addReply(
    conversations,
    createFreeTextReply(
      typedMessage,
      nodeId,
      matchingMatcher?.postbackData || ""
    )
  );
};
function processFreeTextResponse(conversations, flow, reply) {
  const typedMessage = reply.label.toLocaleLowerCase();
  const currentNode = findActiveRichCardNode(flow, conversations);
  if (!currentNode)
    return addReply(conversations, {
      ...reply,
      renderable: true
    });
  const currentCard = currentNode.data;
  const freeTextResponsesData = currentCard.freeTextResponses?.[0];
  const currentMode = freeTextResponsesData?.mode;
  const nodeId = currentNode.id;
  const handleFreeText = (handler) => () => handler(
    conversations,
    typedMessage,
    freeTextResponsesData,
    nodeId
  );
  const matchersMap = {
    ["EQUALS_TO" /* EQUALS_TO */]: handleFreeText(handleEqualsToMode),
    ["DIFFERENT_FROM" /* DIFFERENT_FROM */]: handleFreeText(handleDifferentFromMode),
    ["FREE_RESPONSE" /* FREE_RESPONSE */]: handleFreeText(handleFreeResponseMode),
    ["REGEX" /* REGEX */]: handleFreeText(handleRegexMode)
  };
  return currentMode && matchersMap[currentMode] ? matchersMap[currentMode]() : addReply(conversations, reply);
}

// src/lib/core/store/rcs-visualizer-state-machine/utils/start-conversation.ts
function startConversation(conversations, flow) {
  const { edges } = flow;
  const startNodeEdge = edges.find(
    (edge) => edge.source === "startNode" && edge.target !== "stopMessageNode"
  );
  if (!startNodeEdge || !startNodeEdge.target) {
    return conversations;
  }
  return [...conversations, { nodeId: startNodeEdge.target, reply: null }];
}

// src/lib/core/store/rcs-visualizer-state-machine/rcs-visualizer-state-machine.ts
var createRcsVisualizerStateMachine = (conversations, flow) => createMachine({
  id: "visualizer",
  initial: "start" /* START */,
  context: {
    conversations,
    flow
  },
  states: {
    start: {
      after: {
        [INITIAL_DELAY_DURATION_IN_MILLISECONDS]: {
          actions: assign({
            conversations: ({ context: { conversations: conversations2, flow: flow2 } }) => startConversation(conversations2, flow2)
          }),
          target: "send" /* SEND */
        }
      }
    },
    polling: {
      on: {
        reply: {
          actions: assign({
            conversations: ({ context: { conversations: conversations2 }, event }) => addReply(conversations2, event.reply)
          }),
          target: "answer" /* ANSWER */
        },
        freeText: {
          actions: assign({
            conversations: ({ context: { conversations: conversations2, flow: flow2 }, event }) => processFreeTextResponse(conversations2, flow2, event.reply)
          }),
          target: "answer" /* ANSWER */
        }
      },
      after: {
        [ON_SENT_DELAY_DURATION_IN_MILLISECONDS]: {
          actions: assign({
            conversations: ({ context: { conversations: conversations2, flow: flow2 } }) => addNodeOnSent(conversations2, flow2)
          }),
          target: "send" /* SEND */
        }
      }
    },
    answer: {
      after: {
        [REPLY_DELAY_DURATION_IN_MILLISECONDS]: {
          actions: assign({
            conversations: ({ context: { conversations: conversations2, flow: flow2 } }) => addNode(conversations2, flow2)
          }),
          target: "polling" /* POLLING */
        }
      }
    },
    send: {
      always: {
        target: "polling" /* POLLING */
      }
    }
  },
  on: {
    refresh: {
      actions: assign({
        conversations: () => INITIAL_CONVERSATIONS,
        flow: ({ event }) => event.flow
      }),
      target: ".start" /* START_REF */
    }
  }
});
var conversationsAtom = atom2(INITIAL_CONVERSATIONS);
var rcsVisualizerStateMachineAtom = atomWithMachine(
  (get) => createRcsVisualizerStateMachine(
    get(conversationsAtom),
    get(flowDataJsonAtom)
  )
);
function useRcsVisualizerStateMachineAtom() {
  return useAtom(rcsVisualizerStateMachineAtom);
}

export {
  PREDEFINED_REGEX,
  getPredefinedRegexById,
  isPredefinedRegexType,
  ChartBar,
  statisticsListToChartData,
  colorByChartBarStatus,
  ActionForm,
  CreateLink,
  RCSDashboardStatisticsMenu,
  PICTURE_TYPE_UPLOAD,
  BaseVisualizer,
  rcsAtom,
  generalRcsAtom,
  flowDataJsonAtom,
  NO_DROPPABLE_ID,
  NoDroppable,
  CarouselConversation,
  MEDIA_TYPE_UPLOAD,
  MediaConversation,
  MessageConversation,
  RichCardConversation,
  StopMessageConversation,
  NODE_TYPES,
  ON_SENT_HANDLE_ID,
  ON_SENT_EDGE_TYPE,
  ON_ANSWER_EDGE_TYPE,
  EDGE_TYPES,
  useRcsVisualizerStateMachineAtom,
  MessageVisualizer,
  TextIconVisualizer
};
